/**
 * AdminDashboard.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Full React + TypeScript conversion of admin_dashboard.html + admin_dashboard.js
 *
 * Requirements fulfilled:
 *  1. Auth Guard — session is verified with the server (GET /api/auth/me.php);
 *     App.tsx also checks this before ever mounting this component.
 *  2. Responsive sidebar with mobile toggle (off-canvas drawer).
 *  3. Dashboard stats (Total / Pending / In Progress / Completed) as React state.
 *  4. Job table with status filter tabs + full-text search section.
 *  5. 5-second polling loop (setInterval in useEffect).
 *  6. Cross-tab storage sync via window.addEventListener('storage', ...).
 *     — Automatically updates the table AND any open modal when the driver app
 *       writes to the same localStorage key (rs_jobs) in another tab.
 *  7. jsPDF + jsPDF-AutoTable PDF export (All / Single / Date range) with the
 *     exact background font pre-warming routine for Sarabun (Thai font).
 *  8. Create Job modal — all fields, job-type toggle (transfer / daily),
 *     Thai date overlay, time formatter, phone formatter, email guard.
 * 10. Delete Job flow — confirm modal, completed-lock guard.
 * 11. Job Detail modal — all data fields, audit log, payment slip review,
 *     signature display, delivery photo + lightbox.
 * 12. Link Share modal (driver URL copy).
 * 13. Export PDF modal.
 * 14. Toast notification system.
 * 15. Live clock (Thai locale).
 *
 * Dependencies (loaded via CDN in the host page or bundled):
 *   - react, react-dom (18+)
 *   - jspdf ^2.5.1         → window.jspdf
 *   - jspdf-autotable ^3.8 → auto-extends jsPDF prototype
 *   - @phosphor-icons/web  → <i class="ph ..."> icons
 *   - Tailwind CSS         → utility classes
 *   - admin_dashboard.css  → custom CSS variables + component overrides
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';

// Tracking panel — Leaflet map for in_progress / completed jobs
import DriverTrackingPanel from './DriverTrackingPanel';
import { authApi, jobsApi, driversApi, uploadApi, ApiError } from './api';
import type { JobsSummary } from './api';
import { usePushNotifications } from './usePushNotifications';

// ─── Logo ──────────────────────────────────────────────────────────────────
// Served from public/assets/ (same file used by LoginView and
// OnlineJobSheetPage) — Vite serves anything under public/ at the site root,
// so this resolves to /assets/richer-services-logo.jpg regardless of route.
const LOGO_SRC = '/assets/richer-services-logo.jpg';

// ─── Constants ───────────────────────────────────────────────────────────────
const REPORT_GENERATED_BY = 'Admin / ส่วนกลาง';

// ─── Types ────────────────────────────────────────────────────────────────────
// `Job`, `CompletionLocation`, and `PaymentSlipRecord` used to be declared
// locally in this file, duplicating (and slowly drifting from) the
// canonical definitions in job.types.ts. They're now imported from there —
// the single source of truth that matches what the PHP API actually
// returns (see helpers.php::job_row_to_json()) — and re-exported below so
// existing `import { type Job, ... } from './AdminDashboard'` call sites
// (JobDetailModal.tsx, useAdminSync.ts) keep working unchanged.
import type { Job, CompletionLocation, PaymentSlipRecord } from './job.types';
export type { Job, CompletionLocation, PaymentSlipRecord };

interface Driver {
  id: string;
  name: string;
  phone: string;
  plate?: string;
}

type ToastType = 'success' | 'info' | 'warn' | 'error';
interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

type ActiveSection = 'dashboard' | 'jobs' | 'search';
type FilterStatus = 'all' | 'pending' | 'in_progress' | 'completed';
type FilterType = 'all' | 'transfer' | 'daily';

// ─── Storage helpers ──────────────────────────────────────────────────────────
// เดิมอ่าน/เขียน localStorage ตรง ๆ — ตอนนี้เรียก PHP API จริง (คุยกับ MySQL
// ผ่าน XAMPP) ทุกฟังก์ชันด้านล่างเป็น async ทั้งหมด แต่ยังคงชื่อเดิมไว้
// เพื่อลดจุดที่ต้องแก้ทั่วทั้งไฟล์นี้ให้น้อยที่สุด
async function safeGetJobs(): Promise<Job[]> {
  try {
    const result = await jobsApi.list<Job>();
    return Array.isArray(result) ? result : [];
  } catch {
    return [];
  }
}
async function safeGetDrivers(): Promise<Driver[]> {
  try {
    const result = await driversApi.list<Driver>();
    return Array.isArray(result) ? result : [];
  } catch {
    return [];
  }
}

// ─── Date / Time helpers ──────────────────────────────────────────────────────
export function formatThaiDateNumeric(isoDate: string): string {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${parseInt(y, 10) + 543}`;
}

const THAI_MONTHS_FULL = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

/**
 * แปลงวันที่แบบ "YYYY-MM-DD..." (รองรับทั้ง ISO ที่คั่นด้วย 'T' และแบบที่ MySQL
 * ส่งมาคั่นด้วยเว้นวรรค เช่น "2026-07-06 13:38:55.135") ให้เป็นรูปแบบไทยเต็ม
 * เช่น "6 กรกฎาคม 2569" — ดึงเฉพาะส่วนวันที่ (YYYY-MM-DD) ด้วย regex ก่อนเสมอ
 * เพื่อไม่ให้เวลา/มิลลิวินาทีที่ต่อท้ายมาปนกับการแยกด้วย '-' เหมือนเดิม
 */
export function formatThaiDateLong(dateLike: string): string {
  if (!dateLike) return '';
  const match = dateLike.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const [, y, m, d] = match;
  const monthName = THAI_MONTHS_FULL[parseInt(m, 10) - 1];
  if (!monthName) return '';
  return `${parseInt(d, 10)} ${monthName} ${parseInt(y, 10) + 543}`;
}

export function formatThaiDateTime(date: Date): string {
  return date.toLocaleString('th-TH', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * แสดงผลวันที่+เวลาแบบ 2 บรรทัด (บรรทัดวันที่ / บรรทัด "เวลา HH:MM:SS")
 * ใช้เฉพาะตอนแสดงผลใน UI เท่านั้น — ไม่ใช้กับค่าที่บันทึกลง DB
 * (ฟิลด์ createdAt ยังคงใช้ formatThaiDateTime แบบบรรทัดเดียวเหมือนเดิม)
 */
export function ThaiDateTimeMultiline({ date }: { date: Date }) {
  const formatted = formatThaiDateTime(date);
  const idx = formatted.indexOf('เวลา');
  if (idx === -1) return <>{formatted}</>;
  return (
    <>
      {formatted.slice(0, idx).trim()}
      <br />
      {formatted.slice(idx)}
    </>
  );
}

function formatThaiDateShort(date: Date): string {
  return date.toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function thDateToISO(thStr: string): string {
  if (!thStr) return '';
  const monthsTH: Record<string, string> = {
    'ม.ค.': '01', 'ก.พ.': '02', 'มี.ค.': '03', 'เม.ย.': '04',
    'พ.ค.': '05', 'มิ.ย.': '06', 'ก.ค.': '07', 'ส.ค.': '08',
    'ก.ย.': '09', 'ต.ค.': '10', 'พ.ย.': '11', 'ธ.ค.': '12',
    Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
    Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
  };
  const parts = thStr.trim().split(/\s+/);
  if (parts.length < 3) return '';
  const day = parts[0].padStart(2, '0');
  const mon = monthsTH[parts[1]] ?? '01';
  let yr = parseInt(parts[2], 10);
  if (yr > 2400) yr -= 543;
  return `${yr}-${mon}-${day}`;
}

function formatThaiTimeTyping(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  let hh = digits.slice(0, 2);
  const mm = digits.slice(2);
  if (parseInt(hh, 10) > 23) hh = '23';
  return `${hh}:${mm}`;
}

function formatThaiPhoneDashes(digits: string): string {
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// ─── เสียงแจ้งเตือนสั้น ๆ (Web Audio — ไม่ต้องมีไฟล์เสียง) ────────────────────
function playNotifyBeep() {
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.26);
    osc.onended = () => { void ctx.close(); };
  } catch { /* เงียบ */ }
}

// ─── Job ID generation ────────────────────────────────────────────────────────
function generateJobId(jobs: Job[]): string {
  const yy = String(new Date().getFullYear()).slice(-2);
  const prefix = `LIM${yy}`;
  let maxSeq = 0;
  jobs.forEach((j) => {
    const m = j.id.match(/^LIM\d{2}(\d{5})$/);
    if (m) {
      const seq = parseInt(m[1], 10);
      if (seq > maxSeq) maxSeq = seq;
    }
  });
  return `${prefix}${String(maxSeq + 1).padStart(5, '0')}`;
}

// ─── Status pill ──────────────────────────────────────────────────────────────
export const STATUS_MAP: Record<string, { cls: string; icon: string; label: string }> = {
  pending: { cls: 'pill-pending', icon: 'ph-clock', label: 'รอเริ่มงาน' },
  in_progress: { cls: 'pill-progress', icon: 'ph-car', label: 'กำลังดำเนินการ' },
  completed: { cls: 'pill-completed', icon: 'ph-check-circle', label: 'เสร็จสิ้น' },
};
const STATUS_LABEL_TH: Record<string, string> = {
  pending: 'รอเริ่มงาน',
  in_progress: 'กำลังดำเนินการ',
  completed: 'เสร็จสิ้น',
};
export const PAYMENT_METHOD_MAP: Record<string, string> = {
  cash: '💵 เงินสด',
  transfer: '🏦 ชำระธนาคารออนไลน์',
  credit_card: '💳 บัตรเครดิต',
  qr: '📱 QR Code / Promptpay',
  invoice: '🧾 วางบิล / Invoice',
  other: 'อื่นๆ',
};

function StatusPill({ status }: { status: string }) {
  const s = STATUS_MAP[status] ?? STATUS_MAP.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${s.cls}`}>
      <i className={`ph-fill ${s.icon}`} />
      {s.label}
    </span>
  );
}

// ─── Driver URL builder ───────────────────────────────────────────────────────
// This is now a single-page app (App.tsx routes purely on the `?token=` query
// param — see App.tsx), so the old admin_dashboard.html → online_job_sheet.html
// filename-swap logic no longer applies. Just point back at the same origin
// with `?token=<driverToken>` and strip any existing query/hash first.
//
// IMPORTANT: this takes the job's random driverToken, NOT its id. The id
// (e.g. "LIM2600001") is sequential and guessable — using it directly as
// the access key would let anyone enumerate other customers' jobs without
// logging in. See helpers.php::generate_driver_token() for details.
function buildJobSheetUrl(driverToken: string): string {
  const base = window.location.href.split(/[?#]/)[0];
  return `${base}?token=${encodeURIComponent(driverToken)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
//  PDF EXPORT — mirrors admin_dashboard.js exactly
// ─────────────────────────────────────────────────────────────────────────────
let __thaiFontCache: { regular: string; bold: string } | null = null;
let __thaiFontFetching: Promise<void> | null = null;

function _arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function ensureThaiFont(doc: any): Promise<void> {
  if (!__thaiFontCache) {
    if (!__thaiFontFetching) {
      const FONT_REGULAR =
        'https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/sarabun/Sarabun-Regular.ttf';
      const FONT_BOLD =
        'https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/sarabun/Sarabun-Bold.ttf';
      __thaiFontFetching = (async () => {
        const [regRes, boldRes] = await Promise.all([
          fetch(FONT_REGULAR),
          fetch(FONT_BOLD),
        ]);
        if (!regRes.ok || !boldRes.ok) {
          __thaiFontFetching = null;
          throw new Error('font-fetch-failed');
        }
        const [regBuf, boldBuf] = await Promise.all([
          regRes.arrayBuffer(),
          boldRes.arrayBuffer(),
        ]);
        __thaiFontCache = {
          regular: _arrayBufferToBase64(regBuf),
          bold: _arrayBufferToBase64(boldBuf),
        };
      })();
    }
    await __thaiFontFetching!;
  }
  doc.addFileToVFS('Sarabun-Regular.ttf', __thaiFontCache!.regular);
  doc.addFont('Sarabun-Regular.ttf', 'Sarabun', 'normal');
  doc.addFileToVFS('Sarabun-Bold.ttf', __thaiFontCache!.bold);
  doc.addFont('Sarabun-Bold.ttf', 'Sarabun', 'bold');
  doc.setFont('Sarabun', 'normal');
}

function pad2(n: number) { return String(n).padStart(2, '0'); }
function todayStamp() {
  const d = new Date();
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
}
function todayDisplay() {
  return new Date().toLocaleString('th-TH', { dateStyle: 'long', timeStyle: 'short' });
}

function drawReportHeader(doc: any, subtitle?: string): number {
  const pw = doc.internal.pageSize.getWidth();
  doc.setFillColor(74, 20, 140);
  doc.roundedRect(40, 28, 30, 30, 6, 6, 'F');
  doc.setFont('Sarabun', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.text('RS', 55, 47, { align: 'center' });
  doc.setTextColor(74, 20, 140);
  doc.setFont('Sarabun', 'bold');
  doc.setFontSize(13);
  doc.text('Richer Services', 78, 40);
  doc.setFontSize(9.5);
  doc.setTextColor(90, 90, 90);
  doc.text('Online Chauffeur Job Sheet System', 78, 52);
  doc.setFontSize(8.5);
  doc.setTextColor(110, 110, 110);
  doc.text(`วันที่ออกรายงาน: ${todayDisplay()}`, pw - 40, 38, { align: 'right' });
  doc.text(`ผู้สร้างรายงาน: ${REPORT_GENERATED_BY}`, pw - 40, 49, { align: 'right' });
  if (subtitle) {
    doc.setFont('Sarabun', 'bold');
    doc.setTextColor(142, 36, 170);
    doc.text(subtitle, pw - 40, 60, { align: 'right' });
  }
  doc.setDrawColor(196, 181, 253);
  doc.setLineWidth(1);
  doc.line(40, 66, pw - 40, 66);
  doc.setTextColor(0, 0, 0);
  return 78;
}

function applyFooters(doc: any) {
  const pc = doc.internal.getNumberOfPages();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pc; i++) {
    doc.setPage(i);
    doc.setDrawColor(230, 225, 240);
    doc.setLineWidth(0.7);
    doc.line(40, ph - 38, pw - 40, ph - 38);
    doc.setFont('Sarabun', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      'Richer Services — Online Chauffeur Job Sheet System (เอกสารลับ ใช้ภายในองค์กร)',
      40,
      ph - 26,
    );
    doc.text(`หน้า ${i} / ${pc}`, pw - 40, ph - 26, { align: 'right' });
  }
}

function drawSummaryBlock(doc: any, jobs: Job[], startY: number, title?: string): number {
  const pw = doc.internal.pageSize.getWidth();
  const total = jobs.length;
  const pending = jobs.filter((j) => j.status === 'pending').length;
  const progress = jobs.filter((j) => j.status === 'in_progress').length;
  const completed = jobs.filter((j) => j.status === 'completed').length;
  doc.setFont('Sarabun', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(74, 20, 140);
  doc.text(title ?? 'สรุปผล', 40, startY);
  const boxW = (pw - 80 - 30) / 4;
  const items = [
    { label: 'ทั้งหมด', value: total, color: [74, 20, 140] as [number,number,number] },
    { label: 'รอเริ่มงาน', value: pending, color: [146, 64, 14] as [number,number,number] },
    { label: 'กำลังดำเนินการ', value: progress, color: [106, 27, 154] as [number,number,number] },
    { label: 'เสร็จสิ้น', value: completed, color: [6, 95, 70] as [number,number,number] },
  ];
  items.forEach((it, idx) => {
    const x = 40 + idx * (boxW + 10);
    doc.setDrawColor(225, 190, 231);
    doc.setFillColor(250, 247, 252);
    doc.roundedRect(x, startY + 8, boxW, 38, 4, 4, 'FD');
    doc.setFont('Sarabun', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(...it.color);
    doc.text(String(it.value), x + 10, startY + 32);
    doc.setFont('Sarabun', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 110);
    doc.text(it.label, x + 10, startY + 41);
  });
  return startY + 58;
}

function drawJobAuditAndSignature(doc: any, job: Job, startY: number): number {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  let y = startY;
  if (y > ph - 160) { doc.addPage(); y = drawReportHeader(doc, 'รายงานใบงาน'); }
  doc.setFont('Sarabun', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(74, 20, 140);
  doc.text(`Audit Log — ${job.id}`, 40, y);
  y += 10;
  doc.autoTable({
    startY: y,
    styles: { font: 'Sarabun', fontSize: 8.5, cellPadding: 5 },
    headStyles: { fillColor: [74, 20, 140], textColor: 255, font: 'Sarabun', fontStyle: 'bold' },
    margin: { left: 40, right: 40 },
    theme: 'grid',
    head: [['เวลาเริ่มงาน', 'GPS เริ่มงาน', 'เวลาจบงาน', 'GPS จบงาน']],
    body: [[
      job.auditStartTime ?? 'ไม่มีข้อมูล',
      job.auditStartGps ?? '—',
      job.auditEndTime ?? 'ไม่มีข้อมูล',
      job.auditEndGps ?? '—',
    ]],
  });
  y = doc.lastAutoTable.finalY + 16;
  doc.setFont('Sarabun', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(74, 20, 140);
  doc.text('ลายเซ็นลูกค้ายืนยันการรับบริการ', 40, y);
  y += 8;
  if (job.signatureDataURI) {
    try {
      const props = doc.getImageProperties(job.signatureDataURI);
      const maxW = 220, maxH = 90;
      const ratio = Math.min(maxW / props.width, maxH / props.height, 1);
      const w = props.width * ratio, h = props.height * ratio;
      const x = (pw - w) / 2;
      doc.setDrawColor(225, 190, 231);
      doc.roundedRect(x - 8, y, w + 16, h + 16, 4, 4, 'S');
      doc.addImage(job.signatureDataURI, 'PNG', x, y + 8, w, h);
      y += h + 28;
    } catch {
      doc.setFont('Sarabun', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text('ไม่สามารถแสดงรูปลายเซ็นได้', 40, y + 10);
      y += 24;
    }
  } else {
    doc.setFont('Sarabun', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('ยังไม่มีลายเซ็น — งานยังไม่เสร็จสิ้น', 40, y + 10);
    y += 24;
  }
  return y;
}

async function generateBulkReportPDF(
  jobs: Job[], subtitle: string, filenameBase: string, includeSignatureAppendix: boolean,
) {
  const { jsPDF } = (window as any).jspdf;
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  await ensureThaiFont(doc);
  let y = drawReportHeader(doc, subtitle);
  doc.autoTable({
    startY: y,
    styles: { font: 'Sarabun', fontSize: 6.8, cellPadding: 3, overflow: 'linebreak', valign: 'middle' },
    headStyles: { fillColor: [74, 20, 140], textColor: 255, font: 'Sarabun', fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [250, 247, 252] },
    margin: { left: 24, right: 24, top: 78 },
    theme: 'grid',
    columnStyles: {
      0: { cellWidth: 46 }, 1: { cellWidth: 52 }, 2: { cellWidth: 40 },
      3: { cellWidth: 40 }, 4: { cellWidth: 28 }, 5: { cellWidth: 28 },
      6: { cellWidth: 44 }, 7: { cellWidth: 46 }, 8: { cellWidth: 38 },
      9: { cellWidth: 32 }, 10: { cellWidth: 80 }, 11: { cellWidth: 36 },
    },
    head: [['รหัสงาน', 'ลูกค้า', 'วันจอง', 'วันใช้งาน', 'เริ่ม', 'จบ', 'คนขับ', 'เบอร์โทร', 'ทะเบียน', 'ไฟลท์', 'รายละเอียดการเดินทาง', 'สถานะ']],
    body: jobs.map((j) => [
      j.id, j.customerName, j.bookingDate, j.serviceDate, j.startTime, j.endTime,
      j.driverName, j.driverPhone, j.plate, j.flightNo ?? '—', j.itinerary,
      STATUS_LABEL_TH[j.status] ?? j.status,
    ]),
    didDrawPage: () => { drawReportHeader(doc, subtitle); },
  });
  y = doc.lastAutoTable.finalY + 24;
  if (y > doc.internal.pageSize.getHeight() - 100) { doc.addPage(); y = drawReportHeader(doc, subtitle); }
  y = drawSummaryBlock(doc, jobs, y);
  if (includeSignatureAppendix) {
    const completedJobs = jobs.filter((j) => j.status === 'completed' || j.signatureDataURI);
    if (completedJobs.length) {
      doc.addPage();
      y = drawReportHeader(doc, 'ภาคผนวก: ลายเซ็นและบันทึกการปฏิบัติงาน');
      completedJobs.forEach((job) => { y = drawJobAuditAndSignature(doc, job, y); });
    }
  }
  applyFooters(doc);
  doc.save(filenameBase);
}

async function generateSingleJobReportPDF(job: Job, allJobs: Job[]) {
  const { jsPDF } = (window as any).jspdf;
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  await ensureThaiFont(doc);
  let y = drawReportHeader(doc, `รายงานใบงาน ${job.id}`);
  doc.autoTable({
    startY: y,
    styles: { font: 'Sarabun', fontSize: 9, cellPadding: 6 },
    headStyles: { fillColor: [74, 20, 140], textColor: 255, font: 'Sarabun', fontStyle: 'bold' },
    theme: 'grid',
    margin: { left: 40, right: 40 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 140, textColor: [74, 20, 140] } },
    body: [
      ['รหัสงาน', job.id],
      ['ชื่อลูกค้า', job.customerName],
      ['วันที่จอง', job.bookingDate ?? ''],
      ['วันที่ใช้งาน', job.serviceDate],
      ['เวลาเริ่มงาน', job.startTime],
      ['เวลาจบงาน', job.endTime],
      ['ชื่อคนขับ', job.driverName],
      ['เบอร์โทรคนขับ', job.driverPhone],
      ['เลขทะเบียนรถ', job.plate],
      ['เลขไฟลท์ (Flight No.)', job.flightNo ?? '—'],
      ['รายละเอียดการเดินทาง', job.itinerary],
      ['สถานะงาน', STATUS_LABEL_TH[job.status] ?? job.status],
    ],
  });
  y = doc.lastAutoTable.finalY + 20;
  y = drawJobAuditAndSignature(doc, job, y);
  if (y > doc.internal.pageSize.getHeight() - 140) {
    doc.addPage(); y = drawReportHeader(doc, `รายงานใบงาน ${job.id}`);
  }
  const driverJobs = allJobs.filter((j) => j.driverName === job.driverName);
  drawSummaryBlock(doc, driverJobs, y + 6, `สรุปผลงาน — ${job.driverName}`);
  applyFooters(doc);
  doc.save(`Job_${job.id}.pdf`);
}

// ─────────────────────────────────────────────────────────────────────────────
//  Toast component
// ─────────────────────────────────────────────────────────────────────────────
const TOAST_STYLES: Record<ToastType, string> = {
  success: 'bg-green-600',
  info: 'bg-brand-800',
  warn: 'bg-amber-600',
  error: 'bg-red-600',
};
const TOAST_ICONS: Record<ToastType, string> = {
  success: 'ph-check-circle',
  info: 'ph-info',
  warn: 'ph-warning-circle',
  error: 'ph-x-circle',
};

function ToastList({ toasts }: { toasts: Toast[] }) {
  return (
    <div
      id="toast-wrap"
      aria-live="polite"
      className="fixed top-4 right-4 z-[60] flex flex-col gap-2"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast ${TOAST_STYLES[t.type]} text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2`}
        >
          <i className={`ph-fill ${TOAST_ICONS[t.type]}`} />
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Modal backdrop wrapper
// ─────────────────────────────────────────────────────────────────────────────
export function ModalBackdrop({
  children, onClose, zClass = 'z-50',
}: {
  children: ReactNode; onClose: () => void; zClass?: string;
}) {
  return (
    <div
      className={`fixed inset-0 bg-brand-900/70 backdrop-blur-sm ${zClass} flex items-center justify-center p-2 sm:p-6 modal-backdrop-pad`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Sidebar
// ─────────────────────────────────────────────────────────────────────────────
function Sidebar({
  open, onClose, section, onSection, onCreateJob, onLogout, username,
}: {
  open: boolean;
  onClose: () => void;
  section: ActiveSection;
  onSection: (s: ActiveSection) => void;
  onCreateJob: () => void;
  onLogout: () => void;
  username: string;
}) {
  const links: { id: ActiveSection | 'create'; label: string; icon: string }[] = [
    { id: 'dashboard', label: 'ภาพรวม', icon: 'ph-squares-four' },
    { id: 'jobs', label: 'รายการใบงาน', icon: 'ph-clipboard-text' },
    { id: 'search', label: 'ค้นหาใบงาน', icon: 'ph-magnifying-glass' },
    { id: 'create', label: 'สร้างใบงานใหม่', icon: 'ph-plus-circle' },
  ];

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-[9] ${open ? 'block' : 'hidden'}`}
        id="sidebar-overlay"
        onClick={onClose}
      />
      <aside
        id="sidebar"
        className={`sidebar-el w-60 min-w-[240px] flex flex-col py-6 px-4 shadow-2xl z-10${open ? ' open' : ''}`}
        style={{ background: 'linear-gradient(175deg, #2C0455 0%, #440E7A 55%, #421C69 100%)' }}
      >
        {/* Logo */}
        <div className="mb-2 px-2 flex items-center gap-3">
          <img
            src={LOGO_SRC}
            alt="Richer Services"
            className="w-12 h-12 rounded-full flex-shrink-0 object-cover"
            style={{ boxShadow: '0 0 0 2px var(--gold), 0 0 0 4px rgba(171,97,246,0.2), 0 4px 12px rgba(0,0,0,0.35)' }}
          />
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-bold text-lg leading-tight">
              Admin<br />Dashboard
            </h1>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--gold-light)' }}>
              ระบบจัดการใบงานออนไลน์
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="ปิดเมนู"
            className="sidebar-close-btn w-8 h-8 flex-shrink-0 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <i className="ph ph-x text-base" />
          </button>
        </div>

        {/* Gold divider */}
        <div
          className="mb-5 mx-2"
          style={{ height: '1px', background: 'linear-gradient(90deg, transparent, var(--gold), transparent)' }}
        />

        {/* Nav */}
        <nav className="flex flex-col gap-1 flex-1">
          {links.map((lk) => (
            <div
              key={lk.id}
              className={`sidebar-link${section === lk.id ? ' active' : ''}`}
              onClick={() => {
                if (lk.id === 'create') { onCreateJob(); }
                else { onSection(lk.id as ActiveSection); }
                onClose();
              }}
            >
              <i className={`ph ${lk.icon}`} /> {lk.label}
            </div>
          ))}
        </nav>

        {/* Admin info */}
        <div style={{ borderTop: '1px solid rgba(171,97,246,0.2)' }} className="pt-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center text-xl">
            <i className="ph-fill ph-user-gear" />
          </div>
          <div className="flex-1 min-w-0">
            <p id="sidebar-username" className="text-white text-xs font-semibold">{username}</p>
            <p className="text-brand-200 text-[10px]">Richer Services HQ</p>
          </div>
          <button
            onClick={onLogout}
            title="ออกจากระบบ"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/50 hover:text-red-300 hover:bg-red-900/30 transition flex-shrink-0"
          >
            <i className="ph ph-sign-out text-base" />
          </button>
        </div>
      </aside>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Job Row (shared between dashboard + all-jobs + search)
// ─────────────────────────────────────────────────────────────────────────────

// ป้ายแยกประเภทงาน: "รับส่ง" (transfer) ใช้สีแบรนด์ม่วงเป็น accent,
// "เช่ารถ" (daily) ใช้โทนกลาง (slate) — ให้อ่านออกว่าอันไหนคือ accent ตามไกด์
function JobTypeChip({ type, className = '' }: { type: 'transfer' | 'daily'; className?: string }) {
  const isTransfer = type === 'transfer';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold whitespace-nowrap ${
        isTransfer
          ? 'bg-brand-50 text-brand-800 border border-brand-200'
          : 'bg-slate-100 text-slate-700 border border-slate-200'
      } ${className}`}
    >
      <i className={`ph-fill ${isTransfer ? 'ph-car-profile' : 'ph-calendar-blank'} text-xs`} />
      {isTransfer ? 'รับส่ง' : 'เช่ารถ'}
    </span>
  );
}

function DashboardJobRow({ job, onClick }: { job: Job; onClick: () => void }) {
  return (
    <div className="job-row px-6 py-4 flex items-center gap-4" onClick={onClick}>
      <div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-800 flex items-center justify-center text-lg flex-shrink-0">
        <i className="ph-fill ph-steering-wheel" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-gray-900 truncate">{job.customerName}</p>
          <JobTypeChip type={job.jobType} />
        </div>
        <p className="text-xs text-gray-400">{job.id} · คนขับ: {job.driverName}</p>
        {job.createdAt && (
          <p className="text-[10px] text-gray-300 mt-0.5">
            <i className="ph ph-calendar-plus inline" /> สร้างเมื่อ {job.createdAt}
          </p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <StatusPill status={job.status} />
        <p className="text-[11px] text-gray-400 mt-1">{job.serviceDate}</p>
      </div>
    </div>
  );
}

function FullJobRow({ job, onClick }: { job: Job; onClick: () => void }) {
  return (
    <div
      className="job-row responsive-row-grid grid grid-cols-8 px-6 py-4 items-center text-sm"
      onClick={onClick}
    >
      <div className="col-span-1 font-mono text-xs text-brand-800 font-bold" data-label="รหัสงาน">{job.id}</div>
      <div className="col-span-1" data-label="ลูกค้า">
        <p className="font-semibold text-gray-900 text-xs leading-tight">{job.customerName}</p>
        <div className="mt-1"><JobTypeChip type={job.jobType} /></div>
      </div>
      <div className="col-span-1 text-xs text-gray-600" data-label="วันใช้งาน">{job.serviceDate}</div>
      <div className="col-span-1 text-xs text-gray-600" data-label="เวลา">
        {job.jobType === 'daily' ? `${job.startTime} – ${job.endTime}` : job.startTime}
      </div>
      <div className="col-span-1 text-xs text-gray-600" data-label="คนขับ">{job.driverName}</div>
      <div className="col-span-1 text-xs text-gray-600" data-label="ทะเบียน">{job.plate}</div>
      <div className="col-span-1 text-xs text-gray-600" data-label="ไฟล์ท">{job.flightNo ?? '—'}</div>
      <div className="col-span-1" data-label="สถานะ"><StatusPill status={job.status} /></div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Job Detail Modal
// ─────────────────────────────────────────────────────────────────────────────
function JobModal({
  job, onClose, onDelete, onCopyLink, onExport, onMarkPaid, onUpdateAmount, allJobs,
}: {
  job: Job;
  onClose: () => void;
  onDelete: (id: string) => void;
  onCopyLink: (id: string) => void;
  onExport: (id: string) => void;
  onMarkPaid: (amountPaid: number, slipDataURI: string) => void;
  onUpdateAmount: (id: string, amount: number) => void;
  allJobs: Job[];
}) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const pillMeta = STATUS_MAP[job.status] ?? STATUS_MAP.pending;

  // ถ้ายอดรวม (amount) ยังไม่ถูกกำหนด หรือเป็น 0 ให้ถือว่า "ยังไม่ระบุยอด"
  // ไม่ใช่ "ค้างชำระ" — ป้องกันแถบสรุปยอด/แถบ progress แสดงผลเพี้ยน
  const totalAmt = job.amount != null && Number(job.amount) > 0 ? Number(job.amount) : null;
  const paidAmt = job.amountPaid != null ? Number(job.amountPaid) : 0;
  const slipNeeded = totalAmt != null && totalAmt - paidAmt > 0;

  // ── Admin-side "mark as paid" form state ──
  // The customer now pays for the booking itself; the admin confirms
  // payment here by entering the amount received and attaching the
  // customer's transfer slip (uploaded from the admin's own device).
  const [paidForm, setPaidForm] = useState(false);
  const [paidAmountInput, setPaidAmountInput] = useState(
    totalAmt != null ? String(totalAmt - paidAmt) : '',
  );
  const [slipPreview, setSlipPreview] = useState<string | null>(null);
  const [slipError, setSlipError] = useState('');
  const MAX_SLIP_BYTES = 5 * 1024 * 1024;

  function handleSlipFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SLIP_BYTES) {
      setSlipError('ไฟล์ใหญ่เกิน 5MB');
      e.target.value = '';
      return;
    }
    setSlipError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (typeof ev.target?.result === 'string') setSlipPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  }

  function submitMarkPaid() {
    const amt = parseFloat(paidAmountInput);
    if (!amt || amt <= 0) return setSlipError('กรุณากรอกยอดที่ลูกค้าชำระ');
    if (!slipPreview) return setSlipError('กรุณาแนบสลิปการโอนของลูกค้า');
    onMarkPaid(amt, slipPreview);
    setPaidForm(false);
    setSlipPreview(null);
    setSlipError('');
  }

  const locked = job.status === 'completed';

  const fmtBookingDate = job.createdAtISO
    ? formatThaiDateLong(job.createdAtISO)
    : job.bookingDate ?? '—';

  const timesText = job.jobType === 'daily'
    ? `รับ ${job.startTime || '—'} / คืน ${job.endTime || '—'}`
    : `เวลารับ ${job.startTime || '—'}`;

  return (
    <>
      <ModalBackdrop onClose={onClose}>
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] modal-shell-maxh flex flex-col">
          {/* Header */}
          <div
            className="text-white px-7 py-5 modal-shell-header-pad flex items-start justify-between flex-shrink-0 flex-wrap gap-3"
            style={{ background: 'linear-gradient(135deg, #2C0455 0%, #421C69 100%)' }}
          >
            <div>
              <p className="text-[10px] uppercase tracking-widest font-semibold mb-1" style={{ color: 'var(--gold-light)' }}>
                Richer Services — Job Detail
              </p>
              <h3 className="text-2xl font-bold">{job.id}</h3>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1.5 text-xs font-bold rounded-full ${pillMeta.cls}`}>
                {pillMeta.label}
              </span>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 transition flex items-center justify-center flex-shrink-0"
              >
                <i className="ph ph-x text-xl" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 p-4 sm:p-7 modal-shell-pad space-y-7">
            {/* Payment summary strip — the number people look for first */}
            <div
              className="relative overflow-hidden rounded-2xl px-5 py-4 flex items-center justify-between flex-wrap gap-4 text-white"
              style={{
                background: 'linear-gradient(135deg, #380A66 0%, #421C69 55%, #6A1FA8 100%)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
              }}
            >
              {/* แสงตกแต่งมุมขวาบน (โทนม่วงแบรนด์ จาง ๆ ให้มีมิติ) */}
              <div
                className="absolute -right-10 -top-12 w-44 h-44 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(171,97,246,0.45), transparent 70%)' }}
              />

              {/* ประเภทงาน (ซ้าย) */}
              <div className="relative flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                  {job.jobType === 'daily'
                    ? <i className="ph-fill ph-calendar-blank text-xl" />
                    : <i className="ph-fill ph-car-profile text-xl" />}
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-semibold">ประเภทงาน</p>
                  <p className="text-base font-bold leading-tight">{job.jobType === 'daily' ? 'รายวัน (เช่ารถ)' : 'รับ-ส่ง'}</p>
                </div>
              </div>

              {/* ยอดเงิน (ขวา — เด่นในกล่องทองอ่อน) */}
              {totalAmt != null && (
                <div className="relative flex items-center gap-3 rounded-xl bg-white/10 border border-white/10 px-4 py-2.5">
                  <span
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.12)', color: 'var(--gold-light)' }}
                  >
                    <i className="ph-fill ph-coins text-lg" />
                  </span>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-semibold">ยอดเงินจำนวนจริง</p>
                    <p className="text-xl font-extrabold leading-tight" style={{ color: 'var(--gold-light)' }}>
                      ฿{totalAmt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Customer info */}
            <FieldSection icon="ph-fill ph-user-circle" title="ข้อมูลลูกค้า">
              <InfoField icon="ph ph-user" label="ชื่อลูกค้า" colSpan={2}>{job.customerName}</InfoField>
              <InfoField icon="ph ph-envelope-simple" label="อีเมลลูกค้า" copyValue={job.customerEmail}>{job.customerEmail || '—'}</InfoField>
              <InfoField icon="ph ph-phone" label="เบอร์โทรลูกค้า" copyValue={job.customerPhone}>{job.customerPhone || '—'}</InfoField>
              <InfoField icon="ph-fill ph-calendar-check" iconColor="var(--gold-dark)" label="วันจอง" colSpan={2}>
                <span style={{ color: '#421C69' }} className="font-bold">{fmtBookingDate}</span>
              </InfoField>
            </FieldSection>

            {/* Schedule */}
            <FieldSection icon="ph-fill ph-clock-countdown" title="กำหนดการเดินทาง">
              <InfoField icon="ph ph-airplane-in-flight" label="วันรับ">{job.pickupDate || job.serviceDate || '—'}</InfoField>
              <InfoField icon="ph ph-airplane-landing" label="วันคืน">{job.returnDate || '—'}</InfoField>
              <InfoField icon="ph ph-clock" label="เวลารับ / คืน" colSpan={job.flightNo ? 1 : 2}>{timesText}</InfoField>
              {job.flightNo && <InfoField icon="ph ph-airplane-tilt" label="เลขไฟลท์ (Flight No.)" copyValue={job.flightNo}>{job.flightNo}</InfoField>}
              <InfoField icon="ph ph-map-trifold" label="รายละเอียดการเดินทาง / หมายเหตุ" colSpan={2}>
                <span className="leading-relaxed font-medium">{job.itinerary}</span>
              </InfoField>
            </FieldSection>

            {/* Driver & vehicle */}
            <FieldSection icon="ph-fill ph-steering-wheel" title="คนขับ & พาหนะ">
              <InfoField icon="ph ph-identification-badge" label="ชื่อคนขับ">{job.driverName}</InfoField>
              <InfoField icon="ph ph-phone" label="เบอร์โทรคนขับ" copyValue={job.driverPhone}>{job.driverPhone}</InfoField>
              <InfoField icon="ph ph-car-profile" label="เลขทะเบียนรถ" copyValue={job.plate}>{job.plate}</InfoField>
              <InfoField icon="ph ph-link" label="คนขับเปิดลิงก์ครั้งแรก">
                {job.driverLinkFirstClickedAt
                  ? <span className="text-green-700"><ThaiDateTimeMultiline date={new Date(job.driverLinkFirstClickedAt)} /></span>
                  : <span className="text-gray-400">ยังไม่เปิดลิงก์</span>}
              </InfoField>
            </FieldSection>

            {/* Audit log */}
            <div>
              <SectionHeading icon="ph-fill ph-map-pin-area" title="บันทึก GPS & Timestamp" />
              <div className="bg-gray-50 rounded-2xl border border-gray-100 p-5 space-y-4">
                <AuditRow
                  label="บันทึกเวลาเริ่มงาน"
                  time={job.auditStartTime}
                  gps={job.auditStartGps}
                  accuracy={job.auditStartAccuracy}
                  pending="รอการกดเริ่มงาน..."
                />
                <div className="border-t border-gray-200" />
                <AuditRow
                  label="บันทึกเวลาจบงาน"
                  time={job.auditEndTime}
                  gps={job.auditEndGps}
                  accuracy={job.auditEndAccuracy}
                  pending="รอการกดจบงาน..."
                />
              </div>
            </div>


            {/* Signature */}
            {job.signatureDataURI ? (
              <div>
                <SectionHeading icon="ph-fill ph-signature" title="ลายเซ็นลูกค้ายืนยันการรับบริการ" />
                <div className="border-2 border-dashed border-brand-200 rounded-2xl p-4 bg-white flex justify-center">
                  <img id="modal-sig-img" src={job.signatureDataURI} alt="Customer Signature" className="max-h-36 object-contain" style={{ mixBlendMode: 'multiply' }} />
                </div>
                <p className="text-[10px] text-gray-400 text-center mt-2">ลายเซ็นดิจิทัล — บันทึกถาวร ไม่สามารถแก้ไขได้</p>
              </div>
            ) : (job.status === 'completed' || job.status === 'in_progress') ? (
              <div className="text-center py-5 border-2 border-dashed border-gray-200 rounded-2xl">
                <i className="ph ph-signature text-3xl text-gray-300" />
                <p className="text-xs text-gray-400 mt-2">ยังไม่มีลายเซ็น — รอคนขับจบงาน</p>
              </div>
            ) : null}

            {/* รูปยืนยันตำแหน่งเริ่มงาน + รูปยืนยันส่งมอบงาน — แสดงคู่กัน */}
            {(job.auditStartPhotoURI || (job.status === 'completed' && job.deliveryPhotoURI) || job.status === 'in_progress') && (
              <div>
                <p className="text-[11px] text-gray-400 uppercase font-semibold tracking-widest mb-3 flex items-center gap-2">
                  <i className="ph-fill ph-camera text-sm" style={{ color: 'var(--gold-dark)' }} />
                  รูปภาพยืนยันการทำงาน
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* เริ่มงาน */}
                  {job.auditStartPhotoURI ? (
                    <div className="border-2 border-brand-200 rounded-2xl overflow-hidden bg-brand-50">
                      <div className="px-4 py-3 border-b border-brand-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className="ph-fill ph-image text-brand-700 text-lg" />
                          <p className="text-sm font-bold text-brand-900">รูปยืนยันเริ่มงาน</p>
                        </div>
                        <span className="px-2 py-0.5 text-[9px] font-bold rounded-full bg-brand-100 text-brand-700 border border-brand-200">✓ ถ่ายแล้ว</span>
                      </div>
                      <div className="p-3">
                        <div className="rounded-xl overflow-hidden border border-brand-200 bg-white shadow-sm mb-2">
                          <img
                            src={job.auditStartPhotoURI}
                            alt="Start Photo"
                            className="w-full object-contain max-h-72 cursor-zoom-in"
                            onClick={() => setLightboxSrc(job.auditStartPhotoURI!)}
                          />
                        </div>
                        {job.auditStartTime && (
                          <p className="text-[10px] text-brand-700 mt-1 flex items-center gap-1">
                            <i className="ph ph-clock text-xs" /> ถ่ายเมื่อ: {job.auditStartTime}
                          </p>
                        )}
                        {job.auditStartGps && (
                          <p className="text-[10px] text-brand-700 mt-0.5 font-mono">
                            <i className="ph-fill ph-map-pin text-xs mr-1" />
                            GPS: {job.auditStartGps}{' '}
                            <a href={`https://www.google.com/maps?q=${job.auditStartGps}`} target="_blank" rel="noopener noreferrer" className="text-brand-800 underline ml-1 font-semibold">แผนที่</a>
                          </p>
                        )}
                      </div>
                    </div>
                  ) : job.status !== 'pending' ? (
                    <div className="text-center py-5 border-2 border-dashed border-brand-200 rounded-2xl bg-brand-50">
                      <i className="ph ph-camera text-3xl text-brand-300" />
                      <p className="text-xs font-semibold text-brand-700 mt-2">ไม่มีรูปยืนยันเริ่มงาน</p>
                    </div>
                  ) : null}

                  {/* จบงาน / ส่งมอบ */}
                  {job.status === 'completed' && job.deliveryPhotoURI ? (
                    <div className="border-2 border-green-200 rounded-2xl overflow-hidden bg-green-50">
                      <div className="px-4 py-3 border-b border-green-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className="ph-fill ph-image text-green-700 text-lg" />
                          <p className="text-sm font-bold text-green-900">รูปยืนยันส่งมอบงาน</p>
                        </div>
                        <span className="px-2 py-0.5 text-[9px] font-bold rounded-full bg-green-100 text-green-700 border border-green-200">✓ ถ่ายแล้ว</span>
                      </div>
                      <div className="p-3">
                        <div className="rounded-xl overflow-hidden border border-green-200 bg-white shadow-sm mb-2">
                          <img
                            src={job.deliveryPhotoURI}
                            alt="Delivery Photo"
                            className="w-full object-contain max-h-72 cursor-zoom-in"
                            onClick={() => setLightboxSrc(job.deliveryPhotoURI!)}
                          />
                        </div>
                        {job.deliveryPhotoTime && (
                          <p className="text-[10px] text-green-700 mt-1 flex items-center gap-1">
                            <i className="ph ph-clock text-xs" /> ถ่ายเมื่อ: {job.deliveryPhotoTime}
                          </p>
                        )}
                        {job.deliveryPhotoGps && (
                          <p className="text-[10px] text-green-700 mt-0.5 font-mono">
                            <i className="ph-fill ph-map-pin text-xs mr-1" />
                            GPS: {job.deliveryPhotoGps}{' '}
                            <a href={`https://www.google.com/maps?q=${job.deliveryPhotoGps}`} target="_blank" rel="noopener noreferrer" className="text-green-800 underline ml-1 font-semibold">แผนที่</a>
                          </p>
                        )}
                      </div>
                    </div>
                  ) : job.status === 'in_progress' ? (
                    <div className="text-center py-5 border-2 border-dashed border-green-200 rounded-2xl bg-green-50">
                      <i className="ph ph-camera text-3xl text-green-300" />
                      <p className="text-xs font-semibold text-green-700 mt-2">รอคนขับถ่ายรูปยืนยันการส่ง</p>
                      <p className="text-[10px] text-green-500 mt-1">คนขับต้องถ่ายรูปก่อนกดจบงาน</p>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* ── Live / Completion Map (DriverTrackingPanel) ─────────────
                • in_progress → Live Leaflet map, driver marker at the
                                driver's REAL device GPS position, updated
                                in real time (polled from storage every 3s)
                • completed   → Static green completion marker + GPS metadata
                • pending     → renders null (hidden)
            */}
            {(job.status === 'in_progress' || job.status === 'completed') && (
              <DriverTrackingPanel
                job={{
                  id:            job.id,
                  status:        job.status as 'in_progress' | 'completed',
                  auditStartGps: job.auditStartGps,
                  auditEndGps:   job.auditEndGps,
                  auditEndTime:  job.auditEndTime,
                  completionLocation: job.completionLocation as
                    import('./useDriverTracking').CompletionLocation | null | undefined,
                  driverLocation: job.driverLocation,
                }}
              />
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-7 py-4 modal-shell-footer-pad bg-gray-50 flex items-center justify-between flex-wrap gap-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              {!locked && (
                <button
                  onClick={() => onDelete(job.id)}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-xl hover:bg-red-700 transition flex items-center gap-2"
                >
                  <i className="ph-fill ph-trash" /> ลบใบงาน
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
              <button
                onClick={() => onCopyLink(job.id)}
                className="px-4 py-2 bg-white border-2 border-amber-600 text-amber-700 text-sm font-bold rounded-xl transition flex items-center gap-2 hover:bg-amber-50"
              >
                <i className="ph-fill ph-link" />
                คัดลอกลิงก์
              </button>
              <button onClick={onClose} className="px-5 py-2 bg-brand-900 text-white text-sm font-bold rounded-xl hover:bg-brand-800 transition">
                ปิด
              </button>
            </div>
          </div>
        </div>
      </ModalBackdrop>

      {/* Delivery photo lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 bg-black/90 z-[300] flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
            onClick={() => setLightboxSrc(null)}
          >
            <i className="ph ph-x text-xl" />
          </button>
          <img
            src={lightboxSrc}
            alt="Delivery Photo"
            className="max-w-full max-h-full rounded-2xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

function SectionHeading({ icon, title }: { icon: string; title: ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-[11px] text-gray-400 uppercase font-semibold tracking-widest mb-3">
      <i className={`${icon} text-sm`} style={{ color: 'var(--gold-dark)' }} />
      {title}
    </p>
  );
}

function FieldSection({
  icon, title, children,
}: { icon: string; title: ReactNode; children: ReactNode }) {
  return (
    <div>
      <SectionHeading icon={icon} title={title} />
      <div className="modal-fields-grid grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function InfoField({
  label, children, colSpan = 1, icon, iconColor, copyValue,
}: {
  label: ReactNode;
  children: ReactNode;
  colSpan?: 1 | 2;
  icon?: string;
  iconColor?: string;
  copyValue?: string | null;
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    if (!copyValue) return;
    navigator.clipboard?.writeText(copyValue).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div
      className={`group relative bg-brand-50 rounded-xl p-4 border border-brand-100 transition hover:border-brand-200 hover:bg-brand-50/80${colSpan === 2 ? ' col-span-2' : ''}`}
    >
      <p className="flex items-center gap-1.5 text-[10px] text-gray-400 uppercase font-semibold tracking-wider mb-1">
        {icon && <i className={icon} style={{ color: iconColor ?? '#A78BFA' }} />}
        {label}
      </p>
      <p className="text-sm font-bold text-gray-900 break-words pr-5">{children}</p>
      {copyValue && (
        <button
          type="button"
          onClick={handleCopy}
          title="คัดลอก"
          className="absolute top-3 right-3 w-6 h-6 rounded-lg flex items-center justify-center text-gray-300 opacity-0 group-hover:opacity-100 hover:bg-white hover:text-brand-700 hover:shadow-sm transition-all"
        >
          <i className={copied ? 'ph-fill ph-check text-green-600 text-sm' : 'ph ph-copy text-sm'} />
        </button>
      )}
    </div>
  );
}

function AuditRow({
  label, time, gps, accuracy, pending, photoURI, onPhotoClick,
}: {
  label: string; time?: string | null; gps?: string | null; accuracy?: number | null; pending: string;
  /** รูปยืนยันตำแหน่งจริงที่คนขับถ่ายสด (ปัจจุบันมีเฉพาะตอนเริ่มงาน) */
  photoURI?: string | null;
  onPhotoClick?: (src: string) => void;
}) {
  const tier =
    accuracy == null ? null : accuracy <= 50 ? 'good' : accuracy <= 300 ? 'ok' : 'poor';
  const tierClasses =
    tier === 'good'
      ? 'border-green-200 bg-green-50 text-green-700'
      : tier === 'ok'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : 'border-red-200 bg-red-50 text-red-700';
  const tierTitle =
    tier === 'good' ? 'แม่นยำสูง' : tier === 'ok' ? 'แม่นยำปานกลาง' : 'แม่นยำต่ำ (อาจมาจากคอมพิวเตอร์/Wi-Fi)';
  return (
    <div className="flex items-start gap-3">
      <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${time ? 'bg-green-500' : 'bg-gray-300'}`} />
      <div>
        <p className="text-xs font-bold text-gray-900">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{time ?? pending}</p>
        {gps && (
          <p className="flex flex-wrap items-center gap-1 text-[11px] font-mono text-brand-800 mt-1">
            <i className="ph-fill ph-map-pin text-brand-600 mr-1" />
            GPS: {gps}
            {accuracy != null && (
              <span
                className={`inline-flex items-center rounded border px-1 font-mono text-[9px] font-semibold ${tierClasses}`}
                title={tierTitle}
              >
                ±{Math.round(accuracy)}ม.
              </span>
            )}
            <a href={`https://www.google.com/maps?q=${gps}`} target="_blank" rel="noopener noreferrer" className="text-brand-800 underline ml-1 font-semibold">แผนที่</a>
          </p>
        )}
        {photoURI && (
          <img
            src={photoURI}
            alt="Start location proof"
            className="mt-2 h-28 w-full max-w-xs rounded-lg border border-gray-200 object-cover cursor-zoom-in"
            onClick={() => onPhotoClick?.(photoURI)}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Create Job Modal
// ─────────────────────────────────────────────────────────────────────────────
interface CreateJobFormState {
  jobType: 'transfer' | 'daily';
  customer: string;
  customerEmail: string;
  customerPhone: string;
  bookingDate: string; // ISO
  // transfer
  pickupDate: string;
  returnDate: string;
  startTime: string;
  // daily
  dailyPickupDate: string;
  dailyReturnDate: string;
  dailyStartTime: string;
  endTime: string;
  // common
  driverName: string;
  driverPhone: string;
  plate: string;
  flightNo: string;
  itinerary: string;
  amountPaid: string;
}

const EMPTY_CREATE: CreateJobFormState = {
  jobType: 'transfer', customer: '', customerEmail: '', customerPhone: '',
  bookingDate: '', pickupDate: '', returnDate: '', startTime: '',
  dailyPickupDate: '', dailyReturnDate: '', dailyStartTime: '', endTime: '',
  driverName: '', driverPhone: '', plate: '', flightNo: '',
  itinerary: '', amountPaid: '',
};

function CreateJobModal({
  onClose, onCreated, existingJobs,
}: {
  onClose: () => void;
  onCreated: (job: Job) => void;
  existingJobs: Job[];
}) {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const [form, setForm] = useState<CreateJobFormState>({ ...EMPTY_CREATE, bookingDate: today });
  const [error, setError] = useState('');

  const set = (key: keyof CreateJobFormState) => (val: string) =>
    setForm((f) => ({ ...f, [key]: val }));

  const fmtDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  function submit() {
    const isTransfer = form.jobType === 'transfer';
    const { customer, customerEmail, customerPhone, bookingDate, driverName, driverPhone, plate, flightNo, itinerary, amountPaid } = form;
    const pickupDate = isTransfer ? form.pickupDate : form.dailyPickupDate;
    const returnDate = isTransfer ? form.returnDate : form.dailyReturnDate;
    const startTime = isTransfer ? form.startTime : form.dailyStartTime;
    const endTime = isTransfer ? '' : form.endTime;

    if (!customer) return setError('กรุณากรอกชื่อลูกค้า');
    if (!pickupDate) return setError('กรุณาเลือกวันรับ');
    if (!startTime) return setError('กรุณากรอกเวลารับ');
    if (!returnDate) return setError('กรุณาเลือกวันคืน');
    if (!isTransfer && !endTime) return setError('กรุณากรอกเวลาคืน (รายวัน)');
    if (!driverName) return setError('กรุณากรอกชื่อคนขับ');
    if (!driverPhone) return setError('กรุณากรอกเบอร์โทรคนขับ');
    if (!plate) return setError('กรุณากรอกเลขทะเบียนรถ');
    if (!itinerary) return setError('กรุณากรอกรายละเอียดการเดินทาง');
    if (customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail))
      return setError('รูปแบบอีเมลไม่ถูกต้อง');

    setError('');
    const newId = generateJobId(existingJobs);
    const newJob: Job = {
      id: newId,
      jobType: form.jobType,
      customerName: customer,
      customerEmail,
      customerPhone,
      bookingDate: fmtDate(bookingDate),
      createdAt: formatThaiDateTime(now),
      createdAtISO: now.toISOString(),
      serviceDate: fmtDate(pickupDate),
      pickupDate: fmtDate(pickupDate),
      returnDate: fmtDate(returnDate),
      startTime,
      endTime,
      driverName,
      driverPhone,
      flightNo,
      plate,
      itinerary,
      // ยอดชำระทั้งหมด (amount) อ้างอิงจากช่อง "ยอดชำระจริง" ที่กรอกตอนสร้าง
      // ใบงานเลย โดยอัตโนมัติ — ไม่ต้องมากรอกซ้ำในหน้า Job Detail อีกรอบ
      // (ยังแก้ไขเพิ่มเติมได้ทีหลังผ่านช่องกรอกยอดในหน้า Job Detail ตามปกติ)
      amount: amountPaid !== '' ? parseFloat(amountPaid) : 0,
      amountPaid: amountPaid !== '' ? parseFloat(amountPaid) : null,
      status: 'pending',
      driverLinkFirstClickedAt: null,
      auditStartTime: null, auditStartGps: null, auditStartAccuracy: null,
      auditEndTime: null, auditEndGps: null, auditEndAccuracy: null,
      signatureDataURI: null, deliveryPhotoURI: null,
      deliveryPhotoTime: null, deliveryPhotoGps: null,
    };
    onCreated(newJob);
  }

  const nextId = (() => {
    const yy = String(new Date().getFullYear()).slice(-2);
    const prefix = `LIM${yy}`;
    let maxSeq = 0;
    existingJobs.forEach((j) => {
      const m = j.id.match(/^LIM\d{2}(\d{5})$/);
      if (m) { const s = parseInt(m[1], 10); if (s > maxSeq) maxSeq = s; }
    });
    return `${prefix}${String(maxSeq + 1).padStart(5, '0')}`;
  })();

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[92vh] modal-shell-maxh flex flex-col">
        <div className="text-white px-7 py-5 modal-shell-header-pad flex items-center justify-between flex-shrink-0" style={{ background: 'linear-gradient(135deg, #2C0455 0%, #421C69 100%)' }}>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold mb-1" style={{ color: 'var(--gold-light)' }}>Richer Services — New Job</p>
            <h3 className="text-xl font-bold flex items-center gap-2"><i className="ph-fill ph-plus-circle" /> สร้างใบงานใหม่</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 transition flex items-center justify-center flex-shrink-0">
            <i className="ph ph-x text-xl" />
          </button>
        </div>
        <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent 0%, var(--gold) 30%, var(--gold-light) 50%, var(--gold) 70%, transparent 100%)' }} />

        <div className="overflow-y-auto flex-1 p-7 modal-shell-pad">
          {/* Booking number preview */}
          <div className="mb-5 px-4 py-3 rounded-xl flex items-center gap-3" style={{ background: 'linear-gradient(135deg,#F1E7FE,#F5F0FC)', border: '1px solid #D9C2FA' }}>
            <i className="ph-fill ph-hash text-lg" style={{ color: 'var(--gold-dark)' }} />
            <div>
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">เลขใบงาน (สร้างอัตโนมัติ)</p>
              <p className="text-base font-bold font-mono" style={{ color: '#421C69' }}>{nextId}</p>
            </div>
          </div>

          {/* Job type toggle */}
          <div className="mb-5">
            <label className="block text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-2">ประเภทงาน <span className="text-red-400">*</span></label>
            <div className="flex flex-wrap gap-3">
              {(['transfer', 'daily'] as const).map((t) => (
                <label
                  key={t}
                  className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all"
                  style={{
                    borderColor: form.jobType === t ? '#7B2FBE' : '#E5E7EB',
                    background: form.jobType === t ? '#F1E7FE' : '#F9FAFB',
                  }}
                >
                  <input
                    type="radio"
                    name="cj-job-type"
                    value={t}
                    checked={form.jobType === t}
                    onChange={() => set('jobType')(t)}
                    className="accent-purple-700"
                  />
                  <div>
                    <p className="text-sm font-bold text-gray-900">{t === 'transfer' ? 'รับ-ส่ง' : 'รายวัน'}</p>
                    <p className="text-[11px] text-gray-500">{t === 'transfer' ? 'รับผู้โดยสาร · มีวันรับ เวลารับ และวันคืน' : 'เช่ารถรายวัน · มีวันรับ-คืน และเวลารับ-คืน'}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 responsive-form-grid">
            <FormField label="ชื่อลูกค้า" required colSpan={2}>
              <input
                type="text" placeholder="ชื่อ-นามสกุล / Company name"
                className="input-base"
                value={form.customer}
                onChange={(e) => set('customer')(e.target.value)}
              />
            </FormField>
            <FormField label="อีเมลลูกค้า">
              <input type="text" inputMode="email" placeholder="example@email.com"
                className="input-base"
                value={form.customerEmail}
                onChange={(e) => set('customerEmail')(e.target.value.replace(/[^\x00-\x7F]/g, ''))}
              />
            </FormField>
            <FormField label="เบอร์โทรลูกค้า">
              <input type="tel" inputMode="numeric" maxLength={12} placeholder="0xx-xxx-xxxx"
                className="input-base"
                value={form.customerPhone}
                onChange={(e) => set('customerPhone')(formatThaiPhoneDashes(e.target.value.replace(/\D/g, '')))}
              />
            </FormField>
            {/* Booking date display */}
            <div className="col-span-2">
              <label className="block text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-1.5">วันที่จอง <span className="text-xs text-gray-400 normal-case font-normal">— กำหนดอัตโนมัติตามเวลาปัจจุบัน</span></label>
              <div className="w-full border border-gray-100 rounded-xl px-3 py-2.5 text-sm bg-gray-50 flex items-center gap-2" style={{ minHeight: 42 }}>
                <i className="ph ph-calendar-check" style={{ color: '#9D6FD9', flexShrink: 0 }} />
                <span className="font-semibold" style={{ color: '#421C69' }}>{formatThaiDateNumeric(today)}</span>
              </div>
            </div>

            {/* Transfer fields */}
            {form.jobType === 'transfer' && (
              <>
                <FormField label="วันรับ" required>
                  <ThaiDateInput id="cj-pickup-date" value={form.pickupDate} min={today} onChange={set('pickupDate')} />
                </FormField>
                <FormField label="เวลารับ" required>
                  <ThaiTimeInput value={form.startTime} onChange={set('startTime')} />
                </FormField>
                <FormField label="วันคืน" required>
                  <ThaiDateInput id="cj-return-date" value={form.returnDate}
                    min={form.pickupDate ? addDays(form.pickupDate, 1) : today}
                    onChange={set('returnDate')} />
                </FormField>
                <div className="flex items-end">
                  <p className="text-[11px] text-gray-400 pb-3"><i className="ph ph-info inline mr-1" />รับ-ส่ง ไม่ต้องระบุเวลาคืน</p>
                </div>
              </>
            )}

            {/* Daily fields */}
            {form.jobType === 'daily' && (
              <>
                <FormField label="วันรับ" required>
                  <ThaiDateInput id="cj-daily-pickup" value={form.dailyPickupDate} min={today} onChange={set('dailyPickupDate')} />
                </FormField>
                <FormField label="วันคืน" required>
                  <ThaiDateInput id="cj-daily-return" value={form.dailyReturnDate}
                    min={form.dailyPickupDate ? addDays(form.dailyPickupDate, 1) : today}
                    onChange={set('dailyReturnDate')} />
                </FormField>
                <FormField label="เวลารับ" required>
                  <ThaiTimeInput value={form.dailyStartTime} onChange={set('dailyStartTime')} />
                </FormField>
                <FormField label="เวลาคืน" required>
                  <ThaiTimeInput value={form.endTime} onChange={set('endTime')} />
                </FormField>
              </>
            )}

            <FormField label="ชื่อคนขับ" required>
              <input type="text" placeholder="ชื่อ-นามสกุล" className="input-base"
                value={form.driverName} onChange={(e) => set('driverName')(e.target.value)} />
            </FormField>
            <FormField label="เบอร์โทรคนขับ" required>
              <input type="tel" inputMode="numeric" maxLength={12} placeholder="0xx-xxx-xxxx" className="input-base"
                value={form.driverPhone}
                onChange={(e) => set('driverPhone')(formatThaiPhoneDashes(e.target.value.replace(/\D/g, '')))} />
            </FormField>
            <FormField label="เลขทะเบียนรถ" required>
              <input type="text" placeholder="เช่น 1กข 1234 กทม" className="input-base font-mono"
                value={form.plate} onChange={(e) => set('plate')(e.target.value)} />
            </FormField>
            <FormField label="เลขไฟล์ท (Flight No.)">
              <input type="text" placeholder="เช่น TG600, CX700" className="input-base"
                value={form.flightNo} onChange={(e) => set('flightNo')(e.target.value)} />
            </FormField>
            <FormField label="ยอดเงินจำนวนจริง (บาท)" colSpan={2}>
              <div className="relative">
                <input type="number" min="0" step="0.01" placeholder="0.00"
                  className="input-base pr-7 no-spinner"
                  value={form.amountPaid} onChange={(e) => set('amountPaid')(e.target.value)} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold pointer-events-none">฿</span>
              </div>
            </FormField>
            <FormField label="รายละเอียดการเดินทาง / หมายเหตุ (Itinerary)" required colSpan={2}>
              <ItineraryEditor value={form.itinerary} onChange={set('itinerary')} />
            </FormField>
          </div>

          {error && (
            <div className="mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
              <i className="ph-fill ph-warning-circle flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-7 py-4 bg-gray-50 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-gray-400">เลขใบงานจะถูกสร้างอัตโนมัติ ไม่ซ้ำกัน</p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-5 py-2 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-100 transition">ยกเลิก</button>
            <button onClick={submit}
              className="px-6 py-2 text-white text-sm font-bold rounded-xl active:scale-[0.98] transition flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #631FA8, #421C69)', boxShadow: '0 4px 14px rgba(66,28,105,0.3)' }}>
              <i className="ph-fill ph-check-circle" /> สร้างใบงาน
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Delete confirm modal
// ─────────────────────────────────────────────────────────────────────────────
function DeleteConfirmModal({
  job, onClose, onConfirm,
}: { job: Job; onClose: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200] flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-7 py-6 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <i className="ph-fill ph-trash text-3xl text-red-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">ยืนยันการลบใบงาน</h3>
          <p className="text-sm text-gray-500 mb-6">
            ลบใบงาน "{job.id} — {job.customerName}" ออกจากระบบหรือไม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-100 transition">ยกเลิก</button>
            <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-600 text-white text-sm font-bold rounded-xl hover:bg-red-700 transition flex items-center justify-center gap-2">
              <i className="ph-fill ph-trash" /> ลบใบงาน
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Export PDF Modal
// ─────────────────────────────────────────────────────────────────────────────
function ExportModal({
  onClose, allJobs, onToast,
}: { onClose: () => void; allJobs: Job[]; onToast: (msg: string, type: ToastType) => void }) {
  const [selectedJob, setSelectedJob] = useState(allJobs.length ? [...allJobs].reverse()[0]?.id ?? '' : '');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingSel, setLoadingSel] = useState(false);
  const [loadingRange, setLoadingRange] = useState(false);

  async function doExportAll() {
    if (!allJobs.length) { onToast('ไม่มีข้อมูลใบงานให้ Export', 'warn'); return; }
    setLoadingAll(true);
    try {
      await generateBulkReportPDF(allJobs, 'รายงานใบงานทั้งหมด', `Job_Report_${todayStamp()}.pdf`, true);
      onToast('สร้างรายงาน PDF สำเร็จ', 'success');
      onClose();
    } catch (e: any) {
      onToast(e?.message === 'font-fetch-failed' ? 'โหลดฟอนต์ภาษาไทยไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อ' : 'สร้าง PDF ไม่สำเร็จ', 'error');
    } finally { setLoadingAll(false); }
  }

  async function doExportSelected() {
    const job = allJobs.find((j) => j.id === selectedJob);
    if (!job) { onToast('กรุณาเลือกใบงาน', 'warn'); return; }
    setLoadingSel(true);
    try {
      await generateSingleJobReportPDF(job, allJobs);
      onToast(`สร้างรายงาน ${job.id} สำเร็จ`, 'success');
      onClose();
    } catch (e: any) {
      onToast(e?.message === 'font-fetch-failed' ? 'โหลดฟอนต์ภาษาไทยไม่สำเร็จ' : 'สร้าง PDF ไม่สำเร็จ', 'error');
    } finally { setLoadingSel(false); }
  }

  async function doExportRange() {
    if (!dateStart || !dateEnd) { onToast('กรุณาเลือกช่วงวันที่ให้ครบถ้วน', 'warn'); return; }
    const s = new Date(dateStart), e = new Date(dateEnd);
    if (s > e) { onToast('วันเริ่มต้องไม่มากกว่าวันสิ้นสุด', 'warn'); return; }
    const filtered = allJobs.filter((j) => {
      const d = new Date(j.serviceDate);
      return d >= s && d <= new Date(e.getTime() + 86400000 - 1);
    });
    if (!filtered.length) { onToast('ไม่พบใบงานในช่วงวันที่ที่เลือก', 'warn'); return; }
    setLoadingRange(true);
    try {
      const label = `รายงานช่วงวันที่ ${dateStart} ถึง ${dateEnd}`;
      const filename = `Job_Report_${dateStart.split('-').join('')}-${dateEnd.split('-').join('')}.pdf`;
      await generateBulkReportPDF(filtered, label, filename, true);
      onToast(`สร้างรายงาน ${filtered.length} ใบงานสำเร็จ`, 'success');
      onClose();
    } catch (e: any) {
      onToast(e?.message === 'font-fetch-failed' ? 'โหลดฟอนต์ภาษาไทยไม่สำเร็จ' : 'สร้าง PDF ไม่สำเร็จ', 'error');
    } finally { setLoadingRange(false); }
  }

  const btnStyle = 'w-full py-2.5 text-white text-sm font-bold rounded-xl active:scale-[0.98] transition flex items-center justify-center gap-2';
  const btnBg = { background: 'linear-gradient(135deg, #631FA8, #421C69)' };

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] modal-shell-maxh flex flex-col">
        <div className="text-white px-6 py-4 modal-shell-header-pad flex items-center justify-between flex-shrink-0" style={{ background: 'linear-gradient(135deg, #4B0E88, #5B2395)' }}>
          <div className="flex items-center gap-2">
            <i className="ph-fill ph-file-pdf text-2xl" />
            <h3 className="text-lg font-bold">Export PDF — รายงานใบงาน</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition flex-shrink-0">
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 sm:p-6 modal-shell-pad space-y-5">
          {/* All */}
          <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50">
            <p className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-1"><i className="ph-fill ph-stack text-brand-700" /> รายงานทั้งหมด</p>
            <p className="text-xs text-gray-500 mb-3">ส่งออกใบงานทุกรายการที่มีอยู่ในระบบ พร้อมสรุปผลและลายเซ็น</p>
            <button onClick={doExportAll} disabled={loadingAll} className={btnStyle} style={btnBg}>
              {loadingAll ? <><i className="ph ph-circle-notch spin" /> กำลังสร้าง PDF...</> : <><i className="ph-fill ph-download-simple" /> Export ทั้งหมด</>}
            </button>
          </div>

          {/* Selected */}
          <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50">
            <p className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-1"><i className="ph-fill ph-clipboard-text text-brand-700" /> รายงานตามใบงานที่เลือก</p>
            <p className="text-xs text-gray-500 mb-3">เลือกใบงานหนึ่งรายการเพื่อสร้างรายงานแบบละเอียด</p>
            <select
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-brand-600"
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
            >
              {allJobs.length === 0
                ? <option value="">ไม่มีใบงานในระบบ</option>
                : [...allJobs].reverse().map((j) => (
                  <option key={j.id} value={j.id}>{j.id} — {j.customerName} ({STATUS_LABEL_TH[j.status] ?? j.status})</option>
                ))}
            </select>
            <button onClick={doExportSelected} disabled={loadingSel} className={btnStyle} style={btnBg}>
              {loadingSel ? <><i className="ph ph-circle-notch spin" /> กำลังสร้าง PDF...</> : <><i className="ph-fill ph-download-simple" /> Export ใบงานที่เลือก</>}
            </button>
          </div>

          {/* Date range */}
          <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50">
            <p className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-1"><i className="ph-fill ph-calendar-blank text-brand-700" /> รายงานตามช่วงวันที่</p>
            <p className="text-xs text-gray-500 mb-3">กรองใบงานตามวันที่ใช้งาน (วันที่ใช้บริการ)</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-1">ตั้งแต่</label>
                <input type="date" lang="th" className="input-base" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-1">ถึง</label>
                <input type="date" lang="th" className="input-base" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
              </div>
            </div>
            <button onClick={doExportRange} disabled={loadingRange} className={btnStyle} style={btnBg}>
              {loadingRange ? <><i className="ph ph-circle-notch spin" /> กำลังสร้าง PDF...</> : <><i className="ph-fill ph-download-simple" /> Export ตามช่วงวันที่</>}
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Link Share Modal
// ─────────────────────────────────────────────────────────────────────────────
function LinkShareModal({ url, onClose, onToast }: { url: string; onClose: () => void; onToast: (msg: string, type: ToastType) => void }) {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isFile = window.location.protocol === 'file:';

  async function doCopy() {
    let ok = false;
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(url); ok = true; } catch {}
    }
    if (!ok && inputRef.current) {
      inputRef.current.focus(); inputRef.current.select();
      try { ok = document.execCommand('copy'); } catch {}
    }
    if (ok) { setCopied(true); onToast('คัดลอกลิงก์ใบงานคนขับแล้ว', 'success'); }
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <ModalBackdrop onClose={onClose} zClass="z-[70]">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="text-white px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #2C0455 0%, #421C69 100%)' }}>
          <h3 className="text-base font-bold flex items-center gap-2"><i className="ph-fill ph-link" /> ลิงก์ใบงานคนขับ</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition flex items-center justify-center">
            <i className="ph ph-x text-lg" />
          </button>
        </div>
        <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent 0%, var(--gold) 30%, var(--gold-light) 50%, var(--gold) 70%, transparent 100%)' }} />
        <div className="p-6">
          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-2">แท็บลิงก์ด้านล่างเพื่อทดสอบเปิด หรือกดคัดลอกเพื่อส่งให้คนขับ</p>
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="block mb-3 px-4 py-3 rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-800 text-sm font-mono break-all hover:bg-amber-100 transition">
            <i className="ph-fill ph-arrow-square-out mr-1" />{url}
          </a>
          <div className="flex gap-2">
            <input ref={inputRef} type="text" readOnly value={url} onClick={(e) => (e.target as HTMLInputElement).select()}
              className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-mono bg-gray-50 text-gray-700" />
            <button onClick={doCopy}
              className="px-4 py-2.5 bg-brand-700 text-white text-sm font-bold rounded-xl hover:bg-brand-800 transition flex items-center gap-2 whitespace-nowrap">
              {copied ? <><i className="ph-fill ph-check-circle" /> คัดลอกแล้ว</> : <><i className="ph-fill ph-copy" /> คัดลอก</>}
            </button>
          </div>
          {isFile && (
            <div className="mt-3 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200 text-[11px] text-red-700 flex items-start gap-2">
              <i className="ph-fill ph-warning-circle text-sm mt-0.5" />
              <span>ลิงก์นี้เป็นแบบ <code>file://</code> ใช้ทดสอบได้เฉพาะคอมเครื่องนี้เครื่องเดียว — ต้องอัปโหลดไฟล์ขึ้นเว็บโฮสติ้ง (https) ก่อนส่งให้คนขับ</span>
            </div>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Small reusable form components
// ─────────────────────────────────────────────────────────────────────────────
function FormField({ label, required, children, colSpan = 1 }: {
  label: string; required?: boolean; children: ReactNode; colSpan?: 1 | 2;
}) {
  return (
    <div className={colSpan === 2 ? 'col-span-2' : ''}>
      <label className="block text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function ThaiDateInput({ id, value, min, onChange }: { id: string; value: string; min?: string; onChange: (v: string) => void }) {
  return (
    <div className="thai-dt-wrap">
      <input
        id={id}
        type="date"
        lang="th"
        min={min}
        className="thai-date-input input-base"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className={`thai-dt-overlay${!value ? ' is-placeholder' : ''}`} data-thai-for={id}>
        {value ? formatThaiDateNumeric(value) : 'วว/ดด/ปปปป'}
      </span>
    </div>
  );
}

function ThaiTimeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="thai-time-type-wrap">
      <input
        type="text"
        inputMode="numeric"
        maxLength={5}
        placeholder="--:--"
        autoComplete="off"
        className="thai-time-type-input input-base pr-9"
        value={value}
        onChange={(e) => onChange(formatThaiTimeTyping(e.target.value))}
        onBlur={(e) => {
          let v = e.target.value;
          if (!v) return;
          let [h, m] = v.split(':');
          h = (h || '0').padStart(2, '0');
          m = (m || '0').padStart(2, '0');
          if (parseInt(h, 10) > 23) h = '23';
          if (parseInt(m, 10) > 59) m = '59';
          const final = `${h}:${m}`;
          onChange(/^([01]\d|2[0-3]):[0-5]\d$/.test(final) ? final : '');
        }}
      />
      <span className="thai-time-type-suffix">น.</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Itinerary editor — textarea + "insert waypoint separator" button
//  ปุ่มนี้แทรกตัวคั่น "→" ที่ตำแหน่งเคอร์เซอร์ (หรือต่อท้ายถ้าไม่มีโฟกัส)
//  ใช้ตัวคั่นเดียวกับที่ ItineraryTimeline/parseItinerary แยกจริง (→ หรือ ->)
//  ไม่ใช่ "-" เฉยๆ ซึ่งจะไม่ถูกตัดจุดแวะ (ป้องกันปัญหาตัวคั่นที่พิมพ์เองแล้ว error/ไม่ทำงาน)
// ─────────────────────────────────────────────────────────────────────────
function ItineraryEditor({
  value, onChange, placeholder,
}: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const taRef = useRef<HTMLTextAreaElement>(null);

  function insertSeparator() {
    const ta = taRef.current;
    if (!ta) {
      onChange(value.replace(/\s*$/, '') + (value.trim() ? ' → ' : ''));
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const needsSpacer = before.length > 0 && !before.endsWith(' ');
    const insertText = (needsSpacer ? ' ' : '') + '→ ';
    const next = value.slice(0, start) + insertText + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      const pos = start + insertText.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <button
          type="button"
          onClick={insertSeparator}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-bold text-brand-800 transition hover:bg-brand-100 active:scale-[0.97]"
        >
          <i className="ph-fill ph-plus-circle" />
          เพิ่มที่คั่นจุดแวะ (→)
        </button>
        <span className="text-[10px] text-gray-400">แตะปุ่มเพื่อแทรกตัวคั่นระหว่างสถานที่ ไม่ต้องพิมพ์เอง</span>
      </div>
      <textarea
        ref={taRef}
        rows={3}
        placeholder={placeholder ?? 'เช่น Suvarnabhumi Airport → Siam Paragon → The St. Regis Bangkok\nใช้ปุ่มด้านบนเพื่อเพิ่มจุดคั่น → ระหว่างจุดแวะ'}
        className="input-base resize-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

// ─────────────────────────────────────────────────────────────────────────────
//  Search Section
// ─────────────────────────────────────────────────────────────────────────────
function SearchSection({ onOpenJob }: { onOpenJob: (id: string) => void }) {
  const [customer, setCustomer] = useState('');
  const [driver, setDriver] = useState('');
  const [date, setDate] = useState('');
  const [plate, setPlate] = useState('');
  const [flight, setFlight] = useState('');
  const [results, setResults] = useState<Job[] | null>(null);

  // ค้นหาฝั่ง server (ค้นทั้งฐานข้อมูล ไม่ใช่แค่หน้าปัจจุบัน) — กดปุ่ม "ค้นหา" หรือ Enter
  // วันที่ยังกรองฝั่ง client จากผลลัพธ์ที่ได้ (เพราะ service_date เก็บเป็นข้อความไทย)
  function runSearch() {
    if (!customer && !driver && !date && !plate && !flight) { setResults(null); return; }
    jobsApi
      .page<Job>({ customer, driver, plate, flight, limit: 200 })
      .then((res) => {
        let r = res.jobs;
        if (date) {
          r = r.filter((j) => {
            try { return new Date(j.serviceDate).toDateString() === new Date(date).toDateString(); }
            catch { return false; }
          });
        }
        setResults(r);
      })
      .catch(() => setResults([]));
  }

  function clearSearch() {
    setCustomer(''); setDriver(''); setDate(''); setPlate(''); setFlight('');
    setResults(null);
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-100">
        <h3 className="font-semibold text-brand-900 mb-4 flex items-center gap-2">
          <i className="ph ph-magnifying-glass text-brand-800 text-xl" /> ค้นหาใบงาน
        </h3>
        <div className="search-grid grid grid-cols-3 gap-4">
          {[
            { label: 'ชื่อลูกค้า', val: customer, set: setCustomer, ph: 'พิมพ์แล้วกด Enter'},
            { label: 'ชื่อคนขับ', val: driver, set: setDriver, ph: 'พิมพ์แล้วกด Enter'},
            { label: 'เลขทะเบียนรถ', val: plate, set: setPlate, ph: 'เช่น 1กข 1234' },
            { label: 'เลขไฟล์ท (Flight No.)', val: flight, set: setFlight, ph: 'เช่น TG600' },
          ].map((f) => (
            <div key={f.label}>
              <label className="block text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-1.5">{f.label}</label>
              <input type="text" placeholder={f.ph} className="input-base" value={f.val}
                onChange={(e) => f.set(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }} />
            </div>
          ))}
          <div>
            <label className="block text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-1.5">วันที่ใช้งาน</label>
            <ThaiDateInput id="search-service-date" value={date} onChange={(v) => setDate(v)} />
          </div>
        </div>
        <div className="mt-4 flex gap-3">
          <button onClick={runSearch} className="px-6 py-2.5 bg-brand-900 text-white text-sm font-bold rounded-xl hover:bg-brand-800 transition flex items-center gap-2">
            <i className="ph ph-magnifying-glass" /> ค้นหา
          </button>
          <button onClick={clearSearch} className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition">
            ล้างค่า
          </button>
        </div>
      </div>
      <div className="divide-y divide-gray-50 min-h-[100px]">
        {results === null ? (
          <div className="px-6 py-10 text-center text-sm text-gray-400">กรอกเงื่อนไขค้นหาแล้วกดปุ่มค้นหา</div>
        ) : results.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-400">ไม่พบใบงานที่ตรงกับเงื่อนไข</div>
        ) : (
          <>
            <div className="hidden lg:grid grid-cols-8 px-6 py-3 bg-gray-50 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
              {['รหัสงาน', 'ลูกค้า', 'วันใช้งาน', 'เวลา', 'คนขับ', 'ทะเบียน', 'ไฟล์ท', 'สถานะ'].map((h) => <div key={h}>{h}</div>)}
            </div>
            {results.map((j) => (
              <FullJobRow key={j.id} job={j} onClick={() => onOpenJob(j.id)} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main App
// ─────────────────────────────────────────────────────────────────────────────
let _toastCounter = 0;

export interface AdminDashboardProps {
  /**
   * Called when the session is missing/invalid on mount, or after logout.
   * The parent (App.tsx) owns navigation — this component no longer
   * hard-navigates to a 'login.html' file, since that page doesn't exist
   * in this single-page Vite build (legacy multi-page-site leftover).
   */
  onUnauthorized?: () => void;
}

export default function AdminDashboard({ onUnauthorized }: AdminDashboardProps = {}) {
  // ── Auth ──
  const [authed, setAuthed] = useState(false);
  const [username, setUsername] = useState('Admin');

  // ── Push notifications (แจ้งเตือนเมื่อคนขับกดเริ่ม/จบงาน) ──
  const { permission: pushPermission, status: pushStatus, subscribe: subscribePush } = usePushNotifications();

  // ── Core state ──
  const [jobs, setJobs] = useState<Job[]>([]);           // ← หน้าปัจจุบันของลิสต์ "ใบงานทั้งหมด" (ไม่ใช่ทั้งหมดแล้ว)
  const [summary, setSummary] = useState<JobsSummary<Job> | null>(null); // สถิติ + ใบงานล่าสุด (นับที่ DB)
  const [page, setPage] = useState(1);                    // หน้าปัจจุบันของลิสต์
  const [pageTotal, setPageTotal] = useState(0);          // จำนวนใบงานทั้งหมดที่ตรงตัวกรอง
  const [listQ, setListQ] = useState('');                 // คำค้นหาในลิสต์ (server-side)
  const LIST_LIMIT = 50;                                  // จำนวนใบงานต่อหน้า
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [section, setSection] = useState<ActiveSection>('dashboard');
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [typeFilter, setTypeFilter] = useState<FilterType>('all');
  const [clock, setClock] = useState('');
  const [toasts, setToasts] = useState<Toast[]>([]);

  // ── Modal visibility ──
  const [detailJobId, setDetailJobId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [linkShareUrl, setLinkShareUrl] = useState<string | null>(null);

  // ── Toast helper ──
  function showToast(message: string, type: ToastType = 'success') {
    const id = ++_toastCounter;
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2400);
  }

  // ── Auth guard ──
  // Session ตอนนี้อยู่ฝั่งเซิร์ฟเวอร์ (PHP $_SESSION ผ่าน cookie) — App.tsx
  // เป็นคนตรวจสอบก่อนแล้วถึงจะ mount AdminDashboard นี้ขึ้นมา ดังนั้นแค่ยืนยัน
  // อีกครั้งกับ /api/auth/me.php (กันกรณี session หมดอายุระหว่างใช้งานหน้านี้)
  useEffect(() => {
    authApi
      .me()
      .then((s) => {
        setUsername(s.role || s.username || 'Admin');
        setAuthed(true);
      })
      .catch(() => onUnauthorized?.());
  }, [onUnauthorized]);

  // ── โหลดสรุปตัวเลข + ใบงานล่าสุด (เบา — สำหรับการ์ดสถิติ/หน้า Dashboard) ──
  const loadSummary = useCallback(() => {
    jobsApi.summary<Job>().then(setSummary).catch(() => {});
  }, []);

  // ── โหลดใบงาน "หน้าปัจจุบัน" ของลิสต์ (server-side pagination + filter) ──
  const loadPage = useCallback((manual = false) => {
    jobsApi
      .page<Job>({ page, limit: LIST_LIMIT, type: typeFilter, q: listQ })
      .then((res) => {
        setJobs(res.jobs);
        setPageTotal(res.total);
        if (manual) showToast('อัปเดตข้อมูลล่าสุดแล้ว', 'success');
      })
      .catch(() => {});
  }, [page, typeFilter, listQ]);

  // รีเฟรชทั้งสอง (ใช้โดยปุ่มรีเฟรช/หลังสร้าง/ลบใบงาน) — คงชื่อ loadJobs เดิมไว้
  const loadJobs = useCallback((manual = false) => {
    loadSummary();
    loadPage(manual);
  }, [loadSummary, loadPage]);

  // ── Poll สรุปตัวเลขทุก 5 วินาที (เบามาก) ──
  useEffect(() => {
    if (!authed) return;
    loadSummary();
    const id = setInterval(loadSummary, 5000);
    return () => clearInterval(id);
  }, [authed, loadSummary]);

  // ── แจ้งเตือนในเว็บ (in-app): เด้ง toast + เสียง เมื่อคนขับเริ่ม/จบงาน ──
  // ทำงานได้ทุกที่ ไม่ต้องพึ่ง Service Worker/สิทธิ์แจ้งเตือน/VAPID เหมือน push
  // poll เหตุการณ์ทุก 5 วิ แล้วเทียบกับที่เคยเห็น (จำด้วย id|status) ถ้าใหม่ก็เด้ง
  const seenEventsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!authed) return;
    let stopped = false;
    const poll = async () => {
      try {
        const events = await jobsApi.events();
        if (stopped) return;
        const keys = events.map((e) => `${e.id}|${e.status}`);
        if (seenEventsRef.current === null) {
          // โหลดครั้งแรกหลัง login: จำสถานะปัจจุบันไว้เฉย ๆ ไม่เด้งย้อนหลัง
          seenEventsRef.current = new Set(keys);
          return;
        }
        for (const e of events) {
          const k = `${e.id}|${e.status}`;
          if (seenEventsRef.current.has(k)) continue;
          seenEventsRef.current.add(k);
          const who = e.driverName || 'คนขับ';
          if (e.status === 'in_progress') showToast(`🚗 ${who} เริ่มงาน ${e.id}`, 'success');
          else if (e.status === 'completed') showToast(`✅ งานเสร็จสิ้น: ${who} จบงาน ${e.id}`, 'success');
          playNotifyBeep();
        }
      } catch { /* เงียบ — ไม่ให้ error รบกวนการใช้งาน */ }
    };
    poll();
    const iv = setInterval(poll, 5000);
    return () => { stopped = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // ── โหลด/รีเฟรชลิสต์หน้าปัจจุบัน (เปลี่ยนหน้า/ตัวกรอง/คำค้น = โหลดใหม่) ──
  useEffect(() => {
    if (!authed) return;
    loadPage();
    const id = setInterval(() => loadPage(), 5000);
    return () => clearInterval(id);
  }, [authed, loadPage]);

  // เปลี่ยนตัวกรองประเภท/คำค้น → กลับไปหน้าแรกเสมอ กันหน้าเกินช่วง
  useEffect(() => { setPage(1); }, [typeFilter, listQ]);

  // ── Live clock ──
  useEffect(() => {
    function tick() {
      setClock(new Date().toLocaleString('th-TH', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      }));
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Push notifications: ถ้าเคยกดอนุญาตไว้แล้วในเบราว์เซอร์นี้ (permission
  // === 'granted') ให้ re-subscribe อัตโนมัติทุกครั้งที่ล็อกอิน (เผื่อ
  // subscription เก่าหมดอายุ/ถูกลบไปฝั่งเซิร์ฟเวอร์) — ถ้ายังไม่เคยขอสิทธิ์
  // เลย (permission === 'default') จะไม่ auto-prompt ให้ ต้องกดปุ่มกระดิ่งเอง
  useEffect(() => {
    if (!authed) return;
    if (pushPermission === 'granted') void subscribePush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, pushPermission]);

  if (!authed) return null;

  // ── Derived stats ──
  // กันเหนียว: กรอง null/undefined ทิ้งก่อนเสมอ เผื่อ backend ตอบกลับข้อมูล
  // ผิดปกติ (เช่น JSON parse ไม่สำเร็จ) แล้วหลุดเข้ามาปนใน state
  const safeJobs = jobs.filter((j): j is Job => j != null);
  // สถิติ + ใบงานล่าสุด มาจาก summary (นับที่ DB) — ไม่ต้องโหลดใบงานทั้งหมดมานับเอง
  const stats = {
    total: summary?.total ?? 0,
    pending: summary?.pending ?? 0,
    progress: summary?.in_progress ?? 0,
    completed: summary?.completed ?? 0,
  };
  const recentJobs = summary?.recent ?? [];
  // ลิสต์หน้า "ใบงานทั้งหมด" ถูกกรอง/แบ่งหน้า/เรียง (ใหม่→เก่า) จาก server มาแล้ว ใช้ตรง ๆ
  const filteredJobs = safeJobs;
  const pageCount = Math.max(1, Math.ceil(pageTotal / LIST_LIMIT));

  // ── Detail/delete lookup — หาได้ทั้งในหน้าปัจจุบันและใบงานล่าสุด ──
  const jobPool = [...safeJobs, ...(summary?.recent ?? [])];
  const detailJob = detailJobId ? jobPool.find((j) => j.id === detailJobId) ?? null : null;
  const deleteJob = deleteJobId ? jobPool.find((j) => j.id === deleteJobId) ?? null : null;

  // ── Actions ──
  function doLogout() {
    void authApi.logout().finally(() => onUnauthorized?.());
  }

  function handleOpenJob(id: string) {
    setDetailJobId(id);
  }

  function handleDeleteFromModal(id: string) {
    const j = jobPool.find((x) => x.id === id);
    if (!j) return;
    if (j.status === 'completed') { showToast('ใบงานที่เสร็จสิ้นแล้วไม่สามารถลบได้', 'error'); return; }
    setDetailJobId(null);
    setTimeout(() => setDeleteJobId(id), 220);
  }

  function handleConfirmDelete() {
    if (!deleteJobId) return;
    const targetId = deleteJobId;
    const target = jobPool.find((j) => j.id === targetId);
    if (target?.status === 'completed') {
      setDeleteJobId(null);
      showToast('ใบงานนี้เพิ่งถูกปิดเป็น "เสร็จสิ้น" จึงไม่สามารถลบได้', 'error');
      loadJobs();
      return;
    }
    jobsApi
      .remove(targetId)
      .then(() => {
        setJobs((prev) => prev.filter((j) => j.id !== targetId));
        showToast(`ลบใบงาน ${targetId} เรียบร้อยแล้ว`, 'warn');
        loadSummary();
      })
      .catch((e: unknown) => {
        showToast(e instanceof ApiError ? e.message : 'ลบใบงานไม่สำเร็จ', 'error');
        loadJobs();
      })
      .finally(() => setDeleteJobId(null));
  }

  function handleCreated(newJob: Job) {
    jobsApi
      .create<Job>(newJob)
      .then((created) => {
        if (!created || typeof created !== 'object') {
          showToast('สร้างใบงานไม่สำเร็จ (เซิร์ฟเวอร์ตอบกลับข้อมูลผิดปกติ)', 'error');
          loadJobs();
          return;
        }
        loadJobs();
        setCreateOpen(false);
        showToast(`สร้างใบงาน ${created.id} สำเร็จ`, 'success');
        setSection('jobs');
      })
      .catch((e: unknown) => {
        showToast(e instanceof ApiError ? e.message : 'สร้างใบงานไม่สำเร็จ', 'error');
      });
  }

  function handleCopyLink(id: string) {
    // ใช้ driverToken ของใบงาน (ไม่ใช่ id) เป็นกุญแจเข้าถึงลิงก์คนขับเสมอ —
    // ดูเหตุผลที่ buildJobSheetUrl()
    const job = safeJobs.find((j) => j.id === id);
    if (!job?.driverToken) {
      showToast('ใบงานนี้ยังไม่มีลิงก์คนขับ (โทเคนไม่พร้อมใช้งาน) กรุณารีเฟรชแล้วลองใหม่', 'error');
      return;
    }
    const url = buildJobSheetUrl(job.driverToken);
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        showToast('คัดลอกลิงก์ใบงานคนขับแล้ว', 'success');
      }).catch(() => setLinkShareUrl(url));
    } else {
      setLinkShareUrl(url);
    }
  }

  function handleExportFromModal(id: string) {
    const job = safeJobs.find((j) => j.id === id);
    if (!job) return;
    setDetailJobId(null);
    (async () => {
      try {
        await generateSingleJobReportPDF(job, safeJobs);
        showToast(`สร้างรายงาน ${job.id} สำเร็จ`, 'success');
      } catch (e: any) {
        showToast(e?.message === 'font-fetch-failed' ? 'โหลดฟอนต์ไม่สำเร็จ' : 'สร้าง PDF ไม่สำเร็จ', 'error');
      }
    })();
  }

  function handleMarkPaid(amountPaid: number, slipDataURI: string) {
    if (!detailJobId) return;
    const jobId = detailJobId;
    // อัปโหลดรูปสลิปขึ้นเซิร์ฟเวอร์เป็นไฟล์จริงก่อน แล้วค่อยส่ง path ที่ได้
    // ไปบันทึกลงประวัติการชำระเงินของใบงาน (job_payment_slips)
    uploadApi
      .uploadDataUri(jobId, 'slip', slipDataURI)
      .then((upload) => jobsApi.markPaid<Job>(jobId, amountPaid, upload.path))
      .then((updated) => {
        if (!updated || typeof updated !== 'object') {
          showToast('บันทึกการชำระเงินไม่สำเร็จ (เซิร์ฟเวอร์ตอบกลับข้อมูลผิดปกติ)', 'error');
          loadJobs();
          return;
        }
        setJobs((prev) => prev.map((j) => (j.id === jobId ? updated : j)));
        showToast('✓ บันทึกการชำระเงินของลูกค้าแล้ว', 'success');
      })
      .catch((e: unknown) => {
        showToast(e instanceof ApiError ? e.message : 'บันทึกการชำระเงินไม่สำเร็จ', 'error');
      });
  }

  // แก้ไข "ยอดชำระทั้งหมด" (job.amount) ตรง ๆ จากช่องกรอกในแถบสรุปยอดของ
  // Job Detail modal — บันทึกทันทีที่ออกจากช่อง (blur) ไม่ต้องเปิดหน้าแก้ไข
  // ใบงานแยกต่างหาก
  function handleUpdateAmount(jobId: string, amount: number) {
    jobsApi
      .update<Job>(jobId, { amount })
      .then((updated) => {
        if (!updated || typeof updated !== 'object') {
          showToast('บันทึกยอดเงินไม่สำเร็จ (เซิร์ฟเวอร์ตอบกลับข้อมูลผิดปกติ)', 'error');
          loadJobs();
          return;
        }
        setJobs((prev) => prev.map((j) => (j.id === jobId ? updated : j)));
        showToast('✓ บันทึกยอดชำระทั้งหมดแล้ว', 'success');
      })
      .catch((e: unknown) => {
        showToast(e instanceof ApiError ? e.message : 'บันทึกยอดเงินไม่สำเร็จ', 'error');
        loadJobs();
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="font-sans antialiased text-gray-800">
      <ToastList toasts={toasts} />

      <div className="flex h-screen overflow-hidden relative">
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          section={section}
          onSection={(s) => { setSection(s); setSidebarOpen(false); }}
          onCreateJob={() => { setCreateOpen(true); setSidebarOpen(false); }}
          onLogout={doLogout}
          username={username}
        />

        {/* ── MAIN ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar */}
          <header className="bg-white border-b border-gray-100 px-4 sm:px-8 py-4 flex items-center justify-between flex-wrap gap-3 shadow-sm flex-shrink-0">
            <div>
              <button
                onClick={() => setSidebarOpen(true)}
                className="mobile-menu-btn w-9 h-9 items-center justify-center rounded-xl bg-brand-50 border border-brand-200 text-brand-800 mr-3 mb-1"
                aria-label="เปิดเมนู"
              >
                <i className="ph ph-list text-lg" />
              </button>
              <h2 className="text-xl font-bold text-brand-900 inline">
                {{ dashboard: 'ภาพรวมระบบ', jobs: 'รายการใบงานทั้งหมด', search: 'ค้นหาใบงาน' }[section]}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">Online Chauffeur Job Sheet System — v1.0</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap topbar-actions">
              <button
                onClick={() => setCreateOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold active:scale-[0.98] transition"
                style={{ background: 'linear-gradient(135deg, #7726C9, #AB61F6)', boxShadow: '0 4px 14px rgba(171,97,246,0.35), inset 0 1px 0 rgba(255,255,255,0.15)' }}
              >
                <i className="ph-fill ph-plus-circle text-lg" /> สร้างใบงาน
              </button>
              <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-green-500 pulse-dot" />
                <span className="text-xs font-semibold text-green-700">Real-time Monitor</span>
              </div>
              {pushPermission !== 'unsupported' && (
                <button
                  onClick={() => {
                    if (pushPermission === 'denied') {
                      showToast('การแจ้งเตือนถูกบล็อกไว้ กรุณาเปิดสิทธิ์การแจ้งเตือนของเบราว์เซอร์สำหรับเว็บนี้', 'warn');
                      return;
                    }
                    subscribePush().then(() => {
                      if (pushStatus !== 'error') showToast('เปิดการแจ้งเตือนแบบ Push แล้ว', 'success');
                    });
                  }}
                  title={pushPermission === 'granted' ? 'เปิดการแจ้งเตือนอยู่' : 'เปิดการแจ้งเตือนเมื่อคนขับเริ่ม/จบงาน'}
                  className={`w-9 h-9 flex items-center justify-center rounded-xl border transition ${
                    pushPermission === 'granted'
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-brand-50 border-brand-200 text-brand-800 hover:bg-brand-100'
                  }`}
                  aria-label="เปิดการแจ้งเตือน"
                >
                  <i className={`ph-fill ${pushPermission === 'granted' ? 'ph-bell-ringing' : 'ph-bell'} text-lg`} />
                </button>
              )}
              <button
                onClick={() => loadJobs(true)}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-brand-50 border border-brand-200 text-brand-800 hover:bg-brand-100 transition"
                aria-label="รีเฟรชข้อมูล"
              >
                <i className="ph ph-arrows-clockwise text-lg" />
              </button>
              <button
                onClick={doLogout}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition"
              >
                <i className="ph ph-sign-out text-base" />
                <span className="hidden sm:inline">ออกจากระบบ</span>
              </button>
              <div className="text-right topbar-clock">
                <p className="text-xs text-gray-400">วันที่</p>
                <p className="text-sm font-bold text-brand-900">{clock}</p>
              </div>
            </div>
          </header>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto p-8 space-y-8 main-body-pad">

            {/* ═══ DASHBOARD ═══ */}
            {section === 'dashboard' && (
              <section>
                {/* Stat cards */}
                <div className="stat-grid grid grid-cols-4 gap-5 mb-8">
                  <StatCard label="ทั้งหมด" sublabel="ใบงานในระบบ" value={stats.total}
                    icon="ph-fill ph-clipboard-text" iconBg="bg-brand-100 text-brand-800" valueColor="text-brand-900" />
                  <StatCard label="รอเริ่มงาน" sublabel="ใบงาน Pending" value={stats.pending}
                    icon="ph-fill ph-clock" iconBg="bg-amber-50 text-amber-600" valueColor="text-amber-700" />
                  <StatCard label="กำลังดำเนินการ" sublabel="ใบงาน In Progress" value={stats.progress}
                    icon="ph-fill ph-car" iconBg="bg-brand-50 text-brand-600" valueColor="text-brand-800" />
                  <StatCard label="เสร็จสิ้น" sublabel="ใบงาน Completed" value={stats.completed}
                    icon="ph-fill ph-check-circle" iconBg="bg-green-50 text-green-600" valueColor="text-green-700" />
                </div>

                {/* Recent jobs */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ph ph-list-bullets text-brand-800 text-xl" />
                      <h3 className="font-semibold text-brand-900">ใบงานล่าสุด (Real-time)</h3>
                    </div>
                    <button onClick={() => setSection('jobs')} className="text-xs text-brand-600 font-semibold hover:underline">ดูทั้งหมด →</button>
                  </div>
                  <div className="divide-y divide-gray-50 job-table-wrap">
                    {recentJobs.length === 0 ? (
                      <div className="px-6 py-10 text-center text-sm text-gray-400">ยังไม่มีใบงาน</div>
                    ) : recentJobs.map((j) => (
                      <DashboardJobRow key={j.id} job={j} onClick={() => handleOpenJob(j.id)} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* ═══ ALL JOBS ═══ */}
            {section === 'jobs' && (
              <section>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ph ph-clipboard-text text-brand-800 text-xl" />
                      <h3 className="font-semibold text-brand-900">รายการใบงานทั้งหมด</h3>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* ค้นหาฝั่ง server (ค้นทั้งฐานข้อมูล ไม่ใช่แค่หน้าปัจจุบัน) */}
                      <div className="relative">
                        <i className="ph ph-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="text"
                          value={listQ}
                          onChange={(e) => setListQ(e.target.value)}
                          placeholder="ค้นหา รหัส/ลูกค้า/คนขับ/ทะเบียน"
                          className="text-xs pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 focus:border-brand-400 outline-none w-56 max-w-full"
                        />
                      </div>
                      {/* กรองตามประเภทงาน: รับส่ง / เช่ารถ */}
                      <div className="flex gap-2 flex-wrap">
                        {(['all', 'transfer', 'daily'] as FilterType[]).map((t) => (
                          <button
                            key={t}
                            onClick={() => setTypeFilter(t)}
                            className={`filter-btn text-xs px-3 py-1.5 rounded-lg font-semibold transition ${
                              typeFilter === t
                                ? 'bg-brand-100 text-brand-800 border border-brand-300'
                                : 'bg-gray-100 text-gray-600 border border-transparent hover:bg-gray-200'
                            }`}
                          >
                            {{ all: 'ทุกประเภท', transfer: 'รับส่ง', daily: 'เช่ารถ' }[t]}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="job-table-wrap">
                    <div className="hidden lg:grid grid-cols-8 px-6 py-3 bg-gray-50 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider job-table-min">
                      {['รหัสงาน', 'ลูกค้า', 'วันใช้งาน', 'เวลา', 'คนขับ', 'ทะเบียน', 'ไฟล์ท', 'สถานะ'].map((h) => (
                        <div key={h} className="col-span-1">{h}</div>
                      ))}
                    </div>
                    <div className="divide-y divide-gray-50">
                      {filteredJobs.length === 0 ? (
                        <div className="px-6 py-12 text-center text-sm text-gray-400">ไม่พบใบงานที่ตรงกัน</div>
                      ) : filteredJobs.map((j) => (
                        <FullJobRow key={j.id} job={j} onClick={() => handleOpenJob(j.id)} />
                      ))}
                    </div>
                  </div>
                  {/* แถบแบ่งหน้า — ควบคุมจาก server (ไม่โหลดทั้งหมดมาที่เบราว์เซอร์) */}
                  <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
                    <span>
                      {pageTotal === 0
                        ? 'ไม่มีใบงาน'
                        : `แสดง ${(page - 1) * LIST_LIMIT + 1}–${Math.min(page * LIST_LIMIT, pageTotal)} จาก ${pageTotal} ใบงาน`}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <i className="ph ph-caret-left" /> ก่อนหน้า
                      </button>
                      <span className="font-semibold text-brand-900">หน้า {page} / {pageCount}</span>
                      <button
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        disabled={page >= pageCount}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        ถัดไป <i className="ph ph-caret-right" />
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ═══ SEARCH ═══ */}
            {section === 'search' && (
              <SearchSection onOpenJob={handleOpenJob} />
            )}

          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {detailJob && (
        <JobModal
          job={detailJob}
          allJobs={safeJobs}
          onClose={() => setDetailJobId(null)}
          onDelete={handleDeleteFromModal}
          onCopyLink={handleCopyLink}
          onExport={handleExportFromModal}
          onMarkPaid={handleMarkPaid}
          onUpdateAmount={handleUpdateAmount}
        />
      )}

      {createOpen && (
        <CreateJobModal
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
          existingJobs={safeJobs}
        />
      )}

      {deleteJob && (
        <DeleteConfirmModal
          job={deleteJob}
          onClose={() => { setDeleteJobId(null); if (detailJobId) { /* reopen */ } }}
          onConfirm={handleConfirmDelete}
        />
      )}

      {linkShareUrl && (
        <LinkShareModal
          url={linkShareUrl}
          onClose={() => setLinkShareUrl(null)}
          onToast={showToast}
        />
      )}


      {/* Inline style for input-base utility (used inside JSX above) */}
      <style>{`
        .input-base {
          width: 100%;
          border: 1px solid #E5E7EB;
          border-radius: 0.75rem;
          padding: 0.625rem 0.75rem;
          font-size: 0.875rem;
          transition: box-shadow 0.15s;
        }
        .input-base:focus {
          outline: none;
          box-shadow: 0 0 0 2px #631FA8;
          border-color: transparent;
        }
        .spin {
          display: inline-block;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  StatCard helper
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({
  label, sublabel, value, icon, iconBg, valueColor,
}: {
  label: string; sublabel: string; value: number;
  icon: string; iconBg: string; valueColor: string;
}) {
  return (
    <div className="stat-card bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center text-xl`}>
          <i className={icon} />
        </div>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className={`text-3xl font-bold ${valueColor}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{sublabel}</p>
    </div>
  );
}
